import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CryptoPaymentStep from './CryptoPaymentStep';
import BankTransferStep from './BankTransferStep';
import PurchaseRewardPreview from './PurchaseRewardPreview';
import { supabase } from '../lib/supabase';
import { LegalDocumentService } from '../lib/legalDocumentService';
import { calculateBonusShares, calculateTotalShares, getBonusTier, getAllBonusTiers, getNextBonusTier } from '../lib/bonusShareCalculator';
import styles from '../styles/SharePurchaseFlow.module.css';
import { API_BASE_URL, BIRTHDAY_PROMO_CONFIG, isBirthdayPackageId, normalizeBirthdayQty, computeBirthdayBreakdown,
  PRODUCTION_CAPITAL_CAMPAIGN,
  isProductionCapitalCampaignActive,
  isProductionCapitalPackageId,
  listActiveProductionCapitalPackages,
  computeProductionCapitalBreakdown,
  quoteProductionCapitalCart,
  optimizeProductionCapitalSpend,
  adviseProductionCapitalCart, isSingleShareSpecialActive, computeSingleShareSpecialBreakdown, SINGLE_SHARE_SPECIAL_CONFIG } from '../constants';
import type { BirthdayPackageId, ProductionCapitalPackageId } from '../constants';
import { getSessionHeaders } from '../lib/serverSession';
import { extractNumericId } from '../lib/userIdExtractor';
import { acceptPurchasePhase } from '../lib/displayedSharePrice';

interface CurrentPhase {
  id: number;
  phase_number: number;
  phase_name: string;
  price_per_share: number;
  total_shares_available: number;
  shares_sold: number;
  is_active: boolean;
}

interface PaymentNetwork {
  id: string;
  name: string;
  technical: string;
  icon: string;
  wallet_address: string;
}

interface SavedPaymentMethod {
  id: string;
  type: 'crypto' | 'bank_transfer';
  name: string;
  network?: PaymentNetwork;
  wallet_address?: string;
  bank_details?: any;
  is_default: boolean;
}

type PurchaseStep = 'amount' | 'sponsor_assignment' | 'payment_method' | 'crypto_network' | 'crypto_payment' | 'bank_transfer' | 'confirmation' | 'pending';

// SPECIAL PRICING: Module-level constant so all sub-components share one definition
const TEST_USER_ID = 106;
const TEST_PRICE_SINGLE_SHARE = 1; // $1 for 1 share (testing)

// Resume mode for pending payments or resubmit mode for rejected payments
export interface ResumePaymentData {
  paymentId: string;
  amount: number;
  shares: number;
  network: string;
  isResubmit?: boolean; // True for rejected payments (creates new record), false for pending (updates existing)
  isBalancePayment?: boolean; // True for balance payments on underpaid transactions
  originalPaymentId?: string; // ID of the original underpaid payment (for balance payments)
  balanceOwed?: number; // The exact balance amount owed (for validation)
}

interface SharePurchaseFlowProps {
  user?: any;
  onClose?: () => void;
  onPurchaseComplete?: (isFirstPurchase: boolean) => void;
  resumePayment?: ResumePaymentData; // Pre-fill data for resuming/resubmitting
  birthdayPackage?: BirthdayPackageId; // Birthday 2026 locked-quantity package (retired)
  productionCapitalPackage?: ProductionCapitalPackageId; // Production Capital P1–P5
}

const SharePurchaseFlow: React.FC<SharePurchaseFlowProps> = ({ user, onClose, onPurchaseComplete, resumePayment, birthdayPackage: _birthdayPackage, productionCapitalPackage }) => {
  const { t } = useTranslation('common');
  const [step, setStep] = useState<PurchaseStep>('amount');
  const [amount, setAmount] = useState<string>('');
  const [shares, setShares] = useState<string>(''); // NEW: Share-based input
  const [currentPhase, setCurrentPhase] = useState<CurrentPhase | null>(null);

  // Anniversary (Birthday 2026) SUNSET: the Anniversary packages are retired from
  // public use/view. Birthday mode is force-disabled at the source — this hook
  // ALWAYS resolves to null, so isBirthdayMode is always false and every
  // downstream birthday consumer (locked-quantity preview panel, buy/crypto/bank
  // steps, quick-select gating) behaves as a plain purchase. This also neutralises
  // any stale `aureus.birthdayPackage` sessionStorage value from a prior visit so
  // it can no longer force an Anniversary purchase. The BOGO / Single-Share
  // Special path is independent (isSingleShareSpecialActive) and is unaffected.
  // The incoming `birthdayPackage` prop and isBirthdayPromoActive() are
  // intentionally no longer consulted here. Reversible: restore the prior
  // prop/sessionStorage resolution to re-enable the Anniversary flow.
  const [resolvedBirthdayPackage, setResolvedBirthdayPackage] = useState<BirthdayPackageId | null>(null);
  const isBirthdayMode = resolvedBirthdayPackage !== null;
  const [birthdayQty, setBirthdayQty] = useState<number>(() => {
    try {
      const ssQty = typeof window !== 'undefined' ? window.sessionStorage.getItem('aureus.birthdayQty') : null;
      return normalizeBirthdayQty(ssQty) ?? BIRTHDAY_PROMO_CONFIG.minQty;
    } catch { return BIRTHDAY_PROMO_CONFIG.minQty; }
  });
  const birthdayBreakdown = isBirthdayMode
    ? computeBirthdayBreakdown(resolvedBirthdayPackage as BirthdayPackageId, birthdayQty)
    : null;

  // Switch the active birthday package in-flow. Resets the quantity back to the
  // minimum so a qty chosen for one package cannot carry over unexpectedly, and
  // persists the choice to sessionStorage (mirrors the launch-card behaviour).
  // No financial calculation changes — the existing computeBirthdayBreakdown /
  // gift-marker logic simply operates on the newly selected package.
  const selectBirthdayPackage = (packageId: BirthdayPackageId) => {
    if (!isBirthdayPackageId(packageId)) return;
    setResolvedBirthdayPackage(packageId);
    setBirthdayQty(BIRTHDAY_PROMO_CONFIG.minQty);
    try {
      window.sessionStorage.setItem('aureus.birthdayPackage', packageId);
      window.sessionStorage.setItem('aureus.birthdayQty', String(BIRTHDAY_PROMO_CONFIG.minQty));
    } catch { /* sessionStorage unavailable — state already updated */ }
  };

  // Exit birthday (Anniversary) mode in-flow and drop back to the plain
  // single-share purchase path (which, during the Single-Share Special window,
  // shows the BOGO promo notice + bonus preview). Clears the birthday selection
  // + sessionStorage and seeds the free-type shares input at 1 (amount derived
  // from the CURRENT phase price, never hardcoded). No financial payload change:
  // the single-share buyer bonus remains a server-side gift row.
  const clearBirthdayPackage = () => {
    setResolvedBirthdayPackage(null);
    try {
      window.sessionStorage.removeItem('aureus.birthdayPackage');
      window.sessionStorage.removeItem('aureus.birthdayQty');
    } catch { /* sessionStorage unavailable — state already updated */ }
    setShares('1');
    const price = currentPhase?.price_per_share;
    if (typeof price === 'number' && Number.isFinite(price)) {
      setAmount(String(price));
    }
  };

  // Production Capital: the buyer can hold several packages in one order.
  const emptyPcapQtys = (): Record<ProductionCapitalPackageId, number> => ({ P1: 0, P2: 0, P3: 0, P4: 0, P5: 0 });
  const [pcapQtys, setPcapQtys] = useState<Record<ProductionCapitalPackageId, number>>(() => {
    const next = emptyPcapQtys();
    if (productionCapitalPackage && isProductionCapitalPackageId(productionCapitalPackage)) {
      next[productionCapitalPackage] = 1;
      return next;
    }
    if (!isProductionCapitalCampaignActive({ paidSharesSold: 0 })) return next;
    next.P1 = 1;
    return next;
  });
  const isPcapMode = isProductionCapitalCampaignActive({ paidSharesSold: 0 }) || Object.values(pcapQtys).some((qty) => qty > 0);
  const pcapLines = listActiveProductionCapitalPackages()
    .map((pkg) => ({ packageId: pkg.id, qty: Math.floor(Number(pcapQtys[pkg.id]) || 0) }))
    .filter((line) => line.qty > 0);
  const pcapCart = isPcapMode && pcapLines.length
    ? quoteProductionCapitalCart(pcapLines, PRODUCTION_CAPITAL_CAMPAIGN.pricePerPaidShareUsd)
    : null;
  const pcapBreakdown = pcapCart
    ? {
        packageId: pcapCart.lines.map((line) => line.packageId).join('+'),
        name: pcapCart.lines.map((line) => `${line.name} ×${line.qty}`).join(', '),
        paidShares: pcapCart.paidShares,
        priceUsd: pcapCart.costUsd,
        investorBonus: pcapCart.investorBonus,
        investorTotal: pcapCart.investorTotal,
        sponsorBonus: pcapCart.sponsorBonus,
      }
    : null;
  const pcapAdvice = pcapLines.length ? adviseProductionCapitalCart(pcapLines) : null;

  const setPcapLineQty = (packageId: ProductionCapitalPackageId, qty: number) => {
    const max = PRODUCTION_CAPITAL_CAMPAIGN.maxPackageQty || 100;
    const q = Math.max(0, Math.min(max, Math.floor(Number(qty) || 0)));
    setPcapQtys((current) => ({ ...current, [packageId]: q }));
  };

  const applyPcapLayout = (lines: Array<{ packageId: ProductionCapitalPackageId; qty: number }>) => {
    const next = emptyPcapQtys();
    for (const line of lines) {
      if (isProductionCapitalPackageId(line.packageId)) next[line.packageId] = line.qty;
    }
    setPcapQtys(next);
  };

  const pcapShareKey = pcapCart ? `${pcapCart.paidShares}:${pcapCart.costUsd}` : '';
  useEffect(() => {
    if (!isPcapMode) return;
    if (!pcapShareKey) {
      setShares('0');
      setAmount('0');
      return;
    }
    const [paid, cost] = pcapShareKey.split(':');
    setShares(paid);
    setAmount(cost);
  }, [isPcapMode, pcapShareKey]);

  const [_paymentMethod, setPaymentMethod] = useState<'crypto' | 'bank_transfer' | null>(null);
  const [selectedNetwork, setSelectedNetwork] = useState<PaymentNetwork | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [selectedCurrency, setSelectedCurrency] = useState<'USD' | 'ZAR'>('USD');

  // Commission balance state
  const [commissionBalance, setCommissionBalance] = useState<number>(0);
  const [useCommission, setUseCommission] = useState<boolean>(false);
  const [commissionAmountToUse, setCommissionAmountToUse] = useState<string>('');
  const [loadingCommission, setLoadingCommission] = useState<boolean>(true);
  const [commissionError, setCommissionError] = useState<string>('');
  const [purchaseFullyCovered, _setPurchaseFullyCovered] = useState<boolean>(false);

  // Legal document agreement checking
  const [legalCheckComplete, setLegalCheckComplete] = useState(false);
  const [canPurchaseShares, setCanPurchaseShares] = useState(false);
  const [missingAgreements, setMissingAgreements] = useState<string[]>([]);
  const [_legalCheckMessage, setLegalCheckMessage] = useState<string>('');

  // KYC verification checking (MANDATORY for share purchases)
  const [_kycCheckComplete, setKycCheckComplete] = useState(false);
  const [_kycStatus, setKycStatus] = useState<string>('not_started');
  const [_kycMessage, setKycMessage] = useState<string>('');

  // Enhanced state for new features
  const [savedPaymentMethods, setSavedPaymentMethods] = useState<SavedPaymentMethod[]>([]);
  const [, setSelectedSavedMethod] = useState<SavedPaymentMethod | null>(null);
  const [realTimePrice, setRealTimePrice] = useState<number>(0);
  const [quickAmounts, setQuickAmounts] = useState<number[]>([10, 50, 100, 250, 500, 1000, 2500, 5000]);
  const [quickShares] = useState<number[]>([1, 5, 10, 25, 50, 100, 250, 500]); // NEW: Quick share selection
  const [, setIsOneClickMode] = useState(false);

  // Sponsor verification state (mandatory before purchase)
  const [sponsorVerified, setSponsorVerified] = useState<boolean>(false);
  const [sponsorUsername, setSponsorUsername] = useState<string>('');
  const [sponsorValidationResult, setSponsorValidationResult] = useState<{ isValid: boolean; isChecking: boolean; message: string; type: string; userId?: number } | null>(null);
  const [sponsorAssigning, setSponsorAssigning] = useState<boolean>(false);
  const [, setSponsorCheckLoading] = useState<boolean>(false);

  // Payment networks - will be loaded from database
  const [paymentNetworks, setPaymentNetworks] = useState<PaymentNetwork[]>([]);

  // Session storage key for persisting purchase state across app switches
  const PENDING_PURCHASE_KEY = 'aureus_pending_purchase';

  // Extract numeric user ID for validation
  // CRITICAL: Must parse ALL ID formats to prevent "invalid input syntax for type integer" errors
  const getUserIdForStorage = (): number | null => {
    // Helper function to safely parse any ID format to a number
    const parseId = (id: any): number | null => {
      if (!id) return null;

      // If already a number, validate it
      if (typeof id === 'number') {
        return Number.isInteger(id) && id > 0 ? id : null;
      }

      // Convert to string for processing
      const idStr = String(id);

      // Handle "master_assist_XXX" format (admin assistance sessions)
      if (idStr.includes('master_assist_')) {
        const numericPart = idStr.replace(/master_assist_/g, '');
        const parsed = parseInt(numericPart, 10);
        return !isNaN(parsed) && parsed > 0 ? parsed : null;
      }

      // Handle "db_XXX" format (including malformed "db_db_db_XXX")
      if (idStr.includes('db_')) {
        const numericPart = idStr.replace(/db_/g, '');
        const parsed = parseInt(numericPart, 10);
        return !isNaN(parsed) && parsed > 0 ? parsed : null;
      }

      // Try to parse as integer
      const parsed = parseInt(idStr, 10);
      return !isNaN(parsed) && parsed > 0 ? parsed : null;
    };

    try {
      // MUST parse database_user.id - don't return raw value
      const dbUserId = parseId(user?.database_user?.id);
      if (dbUserId) return dbUserId;

      const metadataUserId = parseId(user?.user_metadata?.user_id);
      if (metadataUserId) return metadataUserId;

      const directUserId = parseId(user?.id);
      if (directUserId) return directUserId;
    } catch (e) {
      console.error('Error extracting user ID for storage:', e);
    }
    return null;
  };

  // Helper to check if current step is a payment step
  const isPaymentStep = (currentStep: PurchaseStep): boolean => {
    return ['crypto_payment', 'bank_transfer'].includes(currentStep);
  };

  // Helper to clear pending purchase state from sessionStorage
  const clearPendingPurchaseState = () => {
    try {
      sessionStorage.removeItem(PENDING_PURCHASE_KEY);
      console.log('🗑️ Cleared pending purchase state from session');
    } catch (e) {
      console.error('Error clearing pending purchase state:', e);
    }
  };

  // Reusable async sponsor check — returns true if user has an active sponsor
  const checkUserHasSponsor = async (): Promise<boolean> => {
    const userId = getUserIdForStorage();
    if (!userId) return false;

    // Primary check via referral-data API
    try {
      const response = await fetch('/api/user/referral-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'get_sponsor', userId })
      });
      const result = await response.json();
      if (result.success && result.data?.sponsor) {
        console.log('✅ Sponsor verified (helper):', result.data.sponsor);
        return true;
      }
    } catch (primaryErr) {
      console.warn('⚠️ Primary sponsor check failed (helper), trying fallback:', primaryErr);
    }

    // Fallback: read-only check if user has an active sponsor
    try {
      const checkResponse = await fetch('/api/user/affiliate-transactions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'checkHasSponsor', data: { userId } })
      });
      const checkResult = await checkResponse.json();
      if (checkResult.success && checkResult.hasSponsor) {
        console.log('✅ Sponsor verified via fallback (helper)');
        return true;
      }
    } catch (fallbackErr) {
      console.warn('⚠️ Fallback sponsor check failed (helper):', fallbackErr);
    }

    return false;
  };

  // Restore pending purchase state from sessionStorage on mount
  // This prevents losing the payment screen when switching apps on mobile
  useEffect(() => {
    const restoreState = async () => {
      try {
        const savedState = sessionStorage.getItem(PENDING_PURCHASE_KEY);
        if (!savedState) return;

        const parsed = JSON.parse(savedState);
        const userId = getUserIdForStorage();

        // Only restore if it's for the same user and not expired (30 min max)
        const isExpired = Date.now() - parsed.timestamp > 30 * 60 * 1000;

        if (parsed.userId === userId && !isExpired && parsed.currentStep) {
          console.log('🔄 Restoring pending purchase state from session:', parsed.currentStep);

          // Restore all state variables
          if (parsed.amount) setAmount(parsed.amount);
          if (parsed.shares) setShares(parsed.shares);
          if (parsed.selectedNetwork) setSelectedNetwork(parsed.selectedNetwork);
          if (parsed.paymentMethod) setPaymentMethod(parsed.paymentMethod);
          if (parsed.selectedCurrency) setSelectedCurrency(parsed.selectedCurrency);

          // MANDATORY SPONSOR CHECK before restoring to a payment step
          if (isPaymentStep(parsed.currentStep)) {
            const hasSponsor = await checkUserHasSponsor();
            if (hasSponsor) {
              setSponsorVerified(true);
              setStep(parsed.currentStep);
            } else {
              console.log('⚠️ No sponsor found during session restore — redirecting to sponsor assignment');
              setStep('sponsor_assignment');
            }
          } else {
            setStep(parsed.currentStep);
          }
        } else {
          // Clear expired or mismatched state
          if (savedState) {
            console.log('🗑️ Clearing invalid/expired purchase state');
            sessionStorage.removeItem(PENDING_PURCHASE_KEY);
          }
        }
      } catch (e) {
        console.error('Error restoring pending purchase state:', e);
        sessionStorage.removeItem(PENDING_PURCHASE_KEY);
      }
    };

    // Delay restoration slightly to allow phase data to load first
    const timeoutId = setTimeout(() => { restoreState(); }, 100);
    return () => clearTimeout(timeoutId);
  }, [user]);

  // Save pending purchase state to sessionStorage when on payment steps
  useEffect(() => {
    if (isPaymentStep(step)) {
      try {
        const userId = getUserIdForStorage();
        if (!userId) return;

        const stateToSave = {
          userId,
          amount,
          shares,
          selectedNetwork,
          paymentMethod: _paymentMethod,
          selectedCurrency,
          currentStep: step,
          timestamp: Date.now()
        };
        sessionStorage.setItem(PENDING_PURCHASE_KEY, JSON.stringify(stateToSave));
        console.log('💾 Saved pending purchase state to session:', step);
      } catch (e) {
        // Handle sessionStorage quota errors gracefully
        console.warn('Error saving pending purchase state:', e);
      }
    }
  }, [step, amount, shares, selectedNetwork, _paymentMethod, selectedCurrency, useCommission, commissionAmountToUse, user]);

  // Fetch current phase on component mount and initialize enhanced features
  useEffect(() => {
    fetchCurrentPhase();
    loadSavedPaymentMethods();
    loadPaymentNetworks(); // Load wallet addresses from database

    // Add body class for mobile scrolling fix
    document.body.classList.add('purchase-modal-open');

    return () => {
      // Remove body class when component unmounts
      document.body.classList.remove('purchase-modal-open');
    };
  }, []);

  // Birthday 2026: keep the (locked) shares + amount pinned to the computed
  // package breakdown whenever the quantity or the live phase price changes.
  // The shares input is disabled in birthday mode so the buyer cannot free-type;
  // the amount is always derived from the CURRENT phase price (never hardcoded).
  useEffect(() => {
    if (!isBirthdayMode || !currentPhase || !birthdayBreakdown) return;
    const lockedShares = birthdayBreakdown.baseShares;
    const lockedAmount = lockedShares * currentPhase.price_per_share;
    setShares(String(lockedShares));
    setAmount(String(lockedAmount));
  }, [isBirthdayMode, birthdayQty, currentPhase, birthdayBreakdown]);

  // Handle resumePayment pre-fill when component mounts with resume data
  useEffect(() => {
    const setupResumePayment = async () => {
      if (!resumePayment || paymentNetworks.length === 0) return;

      console.log('📋 Pre-filling form for resume/resubmit:', resumePayment);

      // Set amount and shares
      setAmount(resumePayment.amount.toString());
      setShares(resumePayment.shares.toString());

      // MANDATORY SPONSOR CHECK before resuming any payment
      const hasSponsor = await checkUserHasSponsor();
      if (!hasSponsor) {
        console.log('⚠️ No sponsor found during resume — redirecting to sponsor assignment');
        setStep('sponsor_assignment');
        return;
      }
      setSponsorVerified(true);

      // Find the matching network
      const network = paymentNetworks.find(n =>
        n.id === resumePayment.network ||
        n.name === resumePayment.network ||
        n.technical === resumePayment.network
      );

      if (network) {
        setSelectedNetwork(network);
        setPaymentMethod('crypto');
        // Skip directly to crypto payment step
        setStep('crypto_payment');
      } else if (resumePayment.network?.toLowerCase().includes('bank')) {
        setPaymentMethod('bank_transfer');
        setStep('bank_transfer');
      } else {
        // Network not found, go to network selection
        setPaymentMethod('crypto');
        setStep('crypto_network');
      }
    };

    setupResumePayment();
  }, [resumePayment, paymentNetworks]);

  // Update quick amounts dynamically based on current phase price
  useEffect(() => {
    if (currentPhase?.price_per_share) {
      const pricePerShare = currentPhase.price_per_share;

      // Generate quick amounts that are multiples of the share price
      // Start with 1 share minimum, then common investment amounts
      const dynamicAmounts = [
        pricePerShare,           // 1 share
        pricePerShare * 5,       // 5 shares
        pricePerShare * 10,      // 10 shares
        pricePerShare * 25,      // 25 shares
        pricePerShare * 50,      // 50 shares
        pricePerShare * 100,     // 100 shares
        pricePerShare * 250,     // 250 shares
        pricePerShare * 500      // 500 shares
      ];

      setQuickAmounts(dynamicAmounts);
      console.log('💰 Quick amounts updated for phase price $' + pricePerShare + ':', dynamicAmounts);
    }
  }, [currentPhase]);

  // Check legal document agreements before allowing purchase
  useEffect(() => {
    const checkLegalAgreements = async () => {
      if (!user) {
        setLegalCheckComplete(true);
        setCanPurchaseShares(false);
        setLegalCheckMessage('User not authenticated');
        return;
      }

      try {
        // Use centralized ID extraction (handles db_, UUID, integer formats)
        const userIdToCheck = getUserIdForStorage();
        if (!userIdToCheck) {
          console.warn('⚠️ SHARE PURCHASE: Could not extract numeric user ID for legal check');
          setLegalCheckComplete(true);
          return;
        }

        console.log('🔍 SHARE PURCHASE: Checking legal agreements for user ID:', userIdToCheck, 'Type:', typeof userIdToCheck);

        const result = await LegalDocumentService.canUserPurchaseShares(userIdToCheck);

        console.log('🔍 SHARE PURCHASE: Legal check result:', result);

        setCanPurchaseShares(result.canPurchase);
        setMissingAgreements(result.missingAgreements);
        setLegalCheckMessage(result.message);
        setLegalCheckComplete(true);
      } catch (error) {
        console.error('❌ SHARE PURCHASE: Error checking legal agreements:', error);
        setCanPurchaseShares(false);
        setMissingAgreements(['Terms & Conditions', 'Privacy Policy', 'Legal Disclaimer', 'Share Purchase Agreement']);
        setLegalCheckMessage('Unable to verify legal document agreements. Please ensure all documents are agreed to.');
        setLegalCheckComplete(true);
      }
    };

    checkLegalAgreements();
  }, [user]);

  // Check KYC status before allowing purchase (MANDATORY)
  useEffect(() => {
    const checkKYCStatus = async () => {
      if (!user) {
        setKycCheckComplete(true);
        setKycStatus('not_started');
        setKycMessage('User not authenticated');
        return;
      }

      try {
        // Use centralized ID extraction (handles db_, UUID, integer formats)
        const userId = getUserIdForStorage();
        if (!userId) {
          console.warn('⚠️ SHARE PURCHASE: Could not extract numeric user ID for KYC check');
          setKycCheckComplete(true);
          return;
        }

        console.log('🔍 SHARE PURCHASE: Checking KYC status for user ID:', userId);

        // CRITICAL FIX: Use server-side API endpoint with service_role client
        // instead of direct anon client query which can silently fail due to
        // RLS/session issues, causing completed KYC users to see "not_started"
        const response = await fetch(`${API_BASE_URL}/api/kyc-status?userId=${userId}`, {
          headers: { ...getSessionHeaders() }
        });
        const result = await response.json();

        if (!response.ok || !result.success) {
          console.error('❌ SHARE PURCHASE: KYC API error:', result);
          setKycStatus('not_started');
          setKycMessage('Unable to verify KYC status. Please complete KYC verification before purchasing shares.');
          setKycCheckComplete(true);
          return;
        }

        const status = result.kycStatus || 'not_started';
        setKycStatus(status);
        console.log('✅ SHARE PURCHASE: KYC status set to:', status, '(raw:', result.rawStatus, ')');

        if (status === 'approved') {
          setKycMessage('✅ KYC verification approved. You can purchase shares.');
        } else if (status === 'pending') {
          setKycMessage('✅ KYC submitted and pending admin approval. You can purchase shares while we review your documents.');
        } else if (status === 'rejected') {
          setKycMessage('❌ KYC verification was rejected. Please resubmit your KYC documents.');
        } else {
          setKycMessage('KYC verification required. Please complete KYC verification before purchasing shares.');
        }

        setKycCheckComplete(true);
      } catch (error) {
        console.error('❌ SHARE PURCHASE: Error checking KYC status:', error);
        setKycStatus('not_started');
        setKycMessage('Unable to verify KYC status. Please complete KYC verification before purchasing shares.');
        setKycCheckComplete(true);
      }
    };

    checkKYCStatus();
  }, [user]);

  // Fetch commission balance
  useEffect(() => {
    console.log('🔄 COMMISSION: useEffect triggered, user:', user ? 'exists' : 'null');

    const fetchCommissionBalance = async () => {
      if (!user) {
        console.log('⚠️ COMMISSION: No user object, skipping fetch');
        setLoadingCommission(false);
        return;
      }

      try {
        // Use centralized ID extraction (consistent with rest of component)
        const userId = getUserIdForStorage();

        if (!userId) {
          console.warn('⚠️ COMMISSION: Could not extract user ID from any source');
          setLoadingCommission(false);
          return;
        }

        console.log('💰 COMMISSION: Fetching balance for user ID:', userId, typeof userId);

        // Fetch commission balance via API endpoint (bypasses RLS)
        // Send both userId and email as fallback
        // Add retry logic for mobile network issues
        let retryCount = 0;
        const maxRetries = 2;

        while (retryCount <= maxRetries) {
          try {
            const { getServerSessionId, createServerSession } = await import('../lib/serverSession');
            let sessionId = getServerSessionId();

            // If no session exists, create one on-the-fly (handles expired/missing sessions)
            if (!sessionId && user) {
              const numericId = user?.database_user?.id || userId;
              const userEmail = user?.email || user?.database_user?.email || '';
              if (numericId && userEmail) {
                console.log('🔐 COMMISSION: No session found, creating one for user:', numericId);
                try {
                  const sessionResult = await createServerSession(numericId, userEmail, 'email');
                  if (sessionResult.success) {
                    sessionId = sessionResult.sessionId || null;
                    console.log('✅ COMMISSION: Session created:', sessionId?.substring(0, 8) + '...');
                  } else {
                    console.warn('⚠️ COMMISSION: Session creation failed:', sessionResult.error);
                  }
                } catch (sessErr) {
                  console.warn('⚠️ COMMISSION: Session creation error:', sessErr);
                }
              }
            }

            const response = await fetch('/api/get-commission-balance', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
              },
              body: JSON.stringify({
                userId,
                email: user?.email || user?.database_user?.email,
                username: user?.database_user?.username
              }),
            });

            console.log('📡 COMMISSION: API response status:', response.status, response.ok, 'Attempt:', retryCount + 1);

            if (!response.ok) {
              const errorText = await response.text();
              console.error('❌ COMMISSION: API error:', response.status, errorText);
              throw new Error(`API returned ${response.status}: ${errorText}`);
            }

            // Use response.json() instead of response.text() + JSON.parse()
            // This is more reliable for handling JSON responses
            const data = await response.json();
            console.log('📦 COMMISSION: Parsed API response data:', JSON.stringify(data, null, 2));
            console.log('💵 COMMISSION: data.usdt_balance =', data.usdt_balance, 'type:', typeof data.usdt_balance);
            const balance = parseFloat(data.usdt_balance || '0');
            console.log('✅ COMMISSION: Parsed balance:', balance, 'type:', typeof balance);
            setCommissionBalance(balance);
            return; // Success - exit the retry loop
          } catch (fetchError) {
            console.error(`❌ COMMISSION: Attempt ${retryCount + 1} failed:`, fetchError);

            if (retryCount < maxRetries) {
              console.log(`🔄 COMMISSION: Retrying in ${(retryCount + 1) * 500}ms...`);
              await new Promise(resolve => setTimeout(resolve, (retryCount + 1) * 500));
              retryCount++;
            } else {
              break;
            }
          }
        }

        // All retries failed - try direct Supabase query as fallback
        console.error('❌ COMMISSION: All API retry attempts failed. Trying direct Supabase fallback...');
        setCommissionError('Loading commission balance...');

        try {
          const { data: directCommission, error: directError } = await supabase
            .from('commission_balances')
            .select('usdt_balance')
            .eq('user_id', userId)
            .single();

          if (directError) {
            if (directError.code === 'PGRST116') {
              console.log('ℹ️ COMMISSION: No balance found via direct query');
              setCommissionBalance(0);
              setCommissionError('');
            } else {
              console.error('❌ COMMISSION: Direct query error:', directError);
              setCommissionBalance(0);
              setCommissionError('Unable to load commission balance. You can still purchase with crypto or bank transfer.');
            }
          } else {
            const balance = parseFloat(directCommission?.usdt_balance || 0);
            console.log('✅ COMMISSION: Fallback successful, balance:', balance);
            setCommissionBalance(balance);
            setCommissionError('');
          }
        } catch (fallbackError) {
          console.error('❌ COMMISSION: Fallback also failed:', fallbackError);
          setCommissionBalance(0);
          setCommissionError('Unable to load commission balance. You can still purchase with crypto or bank transfer.');
        }
      } catch (error) {
        console.error('❌ COMMISSION: Error in fetchCommissionBalance:', error);
        setCommissionBalance(0);
        setCommissionError('Unable to load commission balance. You can still purchase with crypto or bank transfer.');
      } finally {
        setLoadingCommission(false);
      }
    };

    fetchCommissionBalance();
  }, [user]);

  // Load saved payment methods
  const loadSavedPaymentMethods = async () => {
    // CRITICAL: Must parse user ID to prevent "invalid input syntax for type integer" errors
    const userId = getUserIdForStorage();
    if (!userId) return;

    try {
      const { data, error } = await supabase
        .from('user_payment_methods')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true);

      if (!error && data) {
        setSavedPaymentMethods(data);
      }
    } catch (error) {
      console.error('Error loading saved payment methods:', error);
    }
  };

  const loadPaymentNetworks = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/public/phase-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'load_payment_networks' })
      });

      const result = await response.json();

      if (!response.ok || !result.data?.wallets) {
        console.error('Error loading payment networks:', result.error || 'No wallets returned');
        setPaymentNetworks([]);
        return;
      }

      const wallets = result.data.wallets;
      if (wallets.length === 0) {
        setPaymentNetworks([]);
        return;
      }

      // Map database records to PaymentNetwork format
      const networks: PaymentNetwork[] = wallets.map((wallet: any) => {
        const networkConfig = getNetworkConfig(wallet.network);
        return {
          id: wallet.network,
          name: networkConfig.name,
          technical: networkConfig.technical,
          icon: networkConfig.icon,
          wallet_address: wallet.wallet_address
        };
      });

      setPaymentNetworks(networks);
    } catch (error) {
      console.error('Error loading payment networks:', error);
    }
  };

  // Helper function to get network configuration
  const getNetworkConfig = (network: string) => {
    const configs: Record<string, { name: string; technical: string; icon: string }> = {
      'BSC': {
        name: 'Binance Smart Chain',
        technical: 'BEP-20',
        icon: '🟡'
      },
      'ETH': {
        name: 'Ethereum Network',
        technical: 'ERC-20',
        icon: '🔵'
      },
      'POL': {
        name: 'Polygon Network',
        technical: 'Polygon',
        icon: '🟣'
      },
      'POLYGON': {
        name: 'Polygon Network',
        technical: 'Polygon',
        icon: '🟣'
      },
      'TRON': {
        name: 'TRON Network',
        technical: 'TRC-20',
        icon: '🔴'
      }
    };

    return configs[network] || {
      name: network,
      technical: network,
      icon: '💰'
    };
  };

  const fetchCurrentPhase = async () => {
    try {
      setLoading(true);

      // A blocked browser store must not skip the live phase request.
      try {
        localStorage.removeItem('aureus_selected_phase');
      } catch (storageError) {
        console.warn('Could not clear stored phase selection:', storageError);
      }

      // Use API endpoint (service role) instead of direct Supabase anon client
      // This bypasses RLS issues that can cause failures on mobile/production
      let accepted = null;
      let lastStatus = 0;
      for (let attempt = 0; attempt < 2 && !accepted; attempt += 1) {
        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, 400));
        }
        const response = await fetch(`${API_BASE_URL}/api/investment-phases/current`, { cache: 'no-store' });
        lastStatus = response.status;
        if (!response.ok) {
          console.error('❌ API error fetching current phase:', response.status, response.statusText);
          continue;
        }
        accepted = acceptPurchasePhase(await response.json());
      }

      if (!accepted) {
        console.error('❌ CRITICAL ERROR: current phase was not usable', lastStatus);
        throw new Error(lastStatus ? `API returned ${lastStatus}` : 'Invalid phase data returned from API');
      }

      console.log('✅ Active phase loaded via API:', {
        id: accepted.id,
        phase_number: accepted.phase_number,
        phase_name: accepted.phase_name,
        price_per_share: accepted.price_per_share
      });

      setCurrentPhase(accepted as CurrentPhase);
      setRealTimePrice(accepted.price_per_share);
      setError('');
    } catch (err) {
      setError('Failed to load current phase information');
      console.error('Error fetching current phase:', err);
    } finally {
      setLoading(false);
    }
  };

  // Check if special pricing applies (user 106 + exactly 1 share)
  // Reuse centralized getUserIdForStorage() instead of duplicate extraction
  const isSpecialPricingUser = getUserIdForStorage() === TEST_USER_ID;

  const getSpecialPricing = (numShares: number): { applies: boolean; price: number } => {
    if (isSpecialPricingUser && numShares === 1) {
      return { applies: true, price: TEST_PRICE_SINGLE_SHARE };
    }
    return { applies: false, price: numShares * (currentPhase?.price_per_share || 0) };
  };

  // NEW: Calculate amount from shares (share-based purchasing)
  const calculateAmount = (sharesValue: string): number => {
    if (!currentPhase || !sharesValue) return 0;
    const numShares = parseInt(sharesValue);
    if (isNaN(numShares) || numShares < 1) return 0;

    // Check for special pricing (user 106 + 1 share = $1)
    const specialPricing = getSpecialPricing(numShares);
    const calculatedAmount = specialPricing.applies ? specialPricing.price : numShares * currentPhase.price_per_share;

    // CRITICAL LOGGING: Track every amount calculation
    console.log('💰 AMOUNT CALCULATION:', {
      shares: numShares,
      pricePerShare: currentPhase.price_per_share,
      calculatedAmount,
      phase: currentPhase.phase_name,
      isSpecialPricing: specialPricing.applies,
      userId: getUserIdForStorage()
    });

    return calculatedAmount;
  };

  // LEGACY: Keep for backward compatibility
  const calculateShares = (amountValue: string): number => {
    if (!currentPhase || !amountValue) return 0;
    const numAmount = parseFloat(amountValue);
    const calculatedShares = Math.floor(numAmount / currentPhase.price_per_share);

    // CRITICAL LOGGING: Track every share calculation to debug pricing issues
    console.log('🧮 SHARE CALCULATION:', {
      amount: numAmount,
      phase: currentPhase.phase_name,
      pricePerShare: currentPhase.price_per_share,
      calculatedShares: calculatedShares,
      timestamp: new Date().toISOString()
    });

    return calculatedShares;
  };





  // NEW: Validate shares input
  const validateShares = (sharesValue: string): string | null => {
    const numShares = parseInt(sharesValue);
    if (isNaN(numShares) || numShares < 1) return 'Please enter a valid number of shares (minimum 1)';
    if (!currentPhase) return 'Phase information not available';

    if (numShares > 50000) return 'Maximum purchase: 50,000 shares per transaction';

    // Check if enough shares are available
    const remainingShares = currentPhase.total_shares_available - currentPhase.shares_sold;
    if (numShares > remainingShares) {
      return `Only ${remainingShares.toLocaleString()} shares remaining in this phase`;
    }

    return null;
  };

  // LEGACY: Keep for backward compatibility
  const validateAmount = (amountValue: string): string | null => {
    const numAmount = parseFloat(amountValue);
    if (isNaN(numAmount)) return 'Please enter a valid amount';
    if (!currentPhase) return 'Phase information not available';

    const minAmount = currentPhase.price_per_share; // Minimum is 1 share
    if (numAmount < minAmount) return `Minimum purchase: $${minAmount.toFixed(2)} for 1 share`;
    // Max is now share-based (50,000 shares), not dollar-based
    const maxAmount = 50000 * currentPhase.price_per_share;
    if (numAmount > maxAmount) return `Maximum purchase: 50,000 shares ($${maxAmount.toLocaleString()})`;

    const calculatedShares = calculateShares(amountValue);
    if (calculatedShares < 1) return `Minimum purchase: $${currentPhase.price_per_share.toFixed(2)} for 1 share`;

    // Check if enough shares are available
    const remainingShares = currentPhase.total_shares_available - currentPhase.shares_sold;
    if (calculatedShares > remainingShares) {
      return `Only ${remainingShares.toLocaleString()} shares remaining in this phase`;
    }

    return null;
  };

  const handleAmountSubmit = async () => {
    // NEW: Validate shares instead of amount
    const validationError = validateShares(shares);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');

    // Validate commission if user wants to use it
    if (useCommission && loadingCommission) {
      setError('Commission balance is still loading. Please wait a moment and try again.');
      return;
    }

    if (useCommission && commissionError) {
      setError('Unable to load commission balance. Please uncheck "Use Commission" or try again later.');
      return;
    }

    // MANDATORY SPONSOR CHECK: No purchase without active sponsor
    if (!sponsorVerified) {
      setSponsorCheckLoading(true);
      try {
        const userId = getUserIdForStorage();
        if (!userId) {
          setError('Unable to verify your account. Please log in again.');
          setSponsorCheckLoading(false);
          return;
        }

        let hasSponsor = false;

        // Primary check via referral-data API
        try {
          const response = await fetch('/api/user/referral-data', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
            body: JSON.stringify({ operation: 'get_sponsor', userId })
          });
          const result = await response.json();
          if (result.success && result.data?.sponsor) {
            hasSponsor = true;
            console.log('✅ Sponsor verified:', result.data.sponsor);
          }
        } catch (primaryErr) {
          console.warn('⚠️ Primary sponsor check failed, trying fallback:', primaryErr);
        }

        // Fallback: read-only check if user has an active sponsor
        if (!hasSponsor) {
          try {
            const checkResponse = await fetch('/api/user/affiliate-transactions', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
              body: JSON.stringify({ operation: 'checkHasSponsor', data: { userId } })
            });
            const checkResult = await checkResponse.json();
            if (checkResult.success && checkResult.hasSponsor) {
              hasSponsor = true;
              console.log('✅ Sponsor verified via fallback (read-only check)');
            }
          } catch (fallbackErr) {
            console.warn('⚠️ Fallback sponsor check also failed:', fallbackErr);
          }
        }

        if (hasSponsor) {
          setSponsorVerified(true);
          setSponsorCheckLoading(false);
        } else {
          // No sponsor — redirect to sponsor assignment step
          setSponsorCheckLoading(false);
          setStep('sponsor_assignment');
          return;
        }
      } catch (err) {
        console.error('❌ Sponsor check failed:', err);
        setSponsorCheckLoading(false);
        setError('Unable to verify sponsor status. Please try again.');
        return;
      }
    }

    // Proceed to payment method selection
    proceedToPayment();
  };

  // Helper to proceed to the correct payment step
  const proceedToPayment = () => {
    // Check if user is from South Africa, Namibia, or Eswatini
    const userCountry = user?.database_user?.country_of_residence || user?.country_of_residence || user?.country_code;
    const isSouthernAfricaUser = ['ZAF', 'NAM', 'SWZ', 'ZA'].includes(userCountry);

    if (isSouthernAfricaUser) {
      // For Southern Africa users, automatically select payment method based on currency
      if (selectedCurrency === 'ZAR') {
        setPaymentMethod('bank_transfer');
        setStep('bank_transfer');
      } else {
        setPaymentMethod('crypto');
        setStep('crypto_network');
      }
    } else {
      // For non-Southern Africa users, go directly to crypto (no bank transfer option)
      setPaymentMethod('crypto');
      setStep('crypto_network');
    }
  };

  // Handle sponsor assignment submission
  const handleSponsorAssignment = async () => {
    if (!sponsorUsername.trim()) {
      setSponsorValidationResult({ isValid: false, isChecking: false, message: 'Please enter a sponsor username', type: 'error' });
      return;
    }

    if (!sponsorValidationResult?.isValid) {
      return;
    }

    setSponsorAssigning(true);
    try {
      const userId = getUserIdForStorage();
      if (!userId) {
        setSponsorValidationResult({ isValid: false, isChecking: false, message: 'Unable to verify your account. Please log in again.', type: 'error' });
        setSponsorAssigning(false);
        return;
      }

      // Step 1: Search for sponsor via assign-sponsor API (uses service role client)
      const searchResponse = await fetch('/api/user/assign-sponsor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'search_sponsor',
          data: { username: sponsorUsername.trim() }
        })
      });
      const searchResult = await searchResponse.json();

      const sponsorData = searchResult.sponsor || searchResult.data?.sponsor;
      if (!searchResponse.ok || !searchResult.success || !sponsorData) {
        setSponsorValidationResult({ isValid: false, isChecking: false, message: searchResult.error || 'Sponsor not found. Please check the username.', type: 'error' });
        setSponsorAssigning(false);
        return;
      }

      const sponsorId = sponsorData.id;

      // Prevent self-sponsoring
      if (sponsorId === userId) {
        setSponsorValidationResult({ isValid: false, isChecking: false, message: 'You cannot be your own sponsor.', type: 'error' });
        setSponsorAssigning(false);
        return;
      }

      // Step 2: Assign sponsor via assign-sponsor API (uses service role client)
      const assignResponse = await fetch('/api/user/assign-sponsor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'assign_sponsor',
          data: { sponsorId, userId }
        })
      });
      const assignResult = await assignResponse.json();

      if (assignResponse.ok && assignResult.success) {
        setSponsorVerified(true);
        setSponsorAssigning(false);
        console.log('✅ Sponsor assigned successfully:', sponsorUsername);
        proceedToPayment();
      } else if (assignResult.error?.includes('active sponsor')) {
        // Sponsor already exists — treat as success and proceed.
        // Matches the backend's "You already have an active sponsor assigned"
        // (and the admin "User already has an active sponsor") regardless of
        // the have/has wording, so an existing-sponsor response can never
        // dead-lock the buyer on the sponsor-entry screen.
        setSponsorVerified(true);
        setSponsorAssigning(false);
        console.log('✅ Sponsor already assigned, proceeding to payment');
        proceedToPayment();
      } else {
        console.error('❌ Sponsor assignment failed:', assignResult.error);
        setSponsorValidationResult({ isValid: false, isChecking: false, message: assignResult.error || 'Failed to assign sponsor. Please try again.', type: 'error' });
        setSponsorAssigning(false);
      }
    } catch (err) {
      console.error('❌ Sponsor assignment failed:', err);
      setSponsorValidationResult({ isValid: false, isChecking: false, message: 'An error occurred. Please try again.', type: 'error' });
      setSponsorAssigning(false);
    }
  };

  const handlePaymentMethodSelect = (method: 'crypto' | 'bank_transfer') => {
    setPaymentMethod(method);
    if (method === 'crypto') {
      setStep('crypto_network');
    } else {
      setStep('bank_transfer');
    }
  };

  const handleNetworkSelect = (network: PaymentNetwork) => {
    setSelectedNetwork(network);
    setStep('crypto_payment');
  };

  // One-click purchase handler
  const handleOneClickPurchase = async (savedMethod: SavedPaymentMethod) => {
    const validationError = validateAmount(amount);
    if (validationError) {
      setError(validationError);
      return;
    }

    // MANDATORY SPONSOR CHECK for one-click purchases too
    if (!sponsorVerified) {
      try {
        const userId = getUserIdForStorage();
        if (userId) {
          let hasSponsor = false;

          // Primary check via referral-data API
          try {
            const response = await fetch('/api/user/referral-data', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
              body: JSON.stringify({ operation: 'get_sponsor', userId })
            });
            const result = await response.json();
            if (result.success && result.data?.sponsor) {
              hasSponsor = true;
            }
          } catch (primaryErr) {
            console.warn('⚠️ Primary sponsor check failed (one-click), trying fallback:', primaryErr);
          }

          // Fallback: read-only check if user has an active sponsor
          if (!hasSponsor) {
            try {
              const checkResponse = await fetch('/api/user/affiliate-transactions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
                body: JSON.stringify({ operation: 'checkHasSponsor', data: { userId } })
              });
              const checkResult = await checkResponse.json();
              if (checkResult.success && checkResult.hasSponsor) {
                hasSponsor = true;
                console.log('✅ Sponsor verified via fallback (one-click, read-only)');
              }
            } catch (fallbackErr) {
              console.warn('⚠️ Fallback sponsor check failed (one-click):', fallbackErr);
            }
          }

          if (hasSponsor) {
            setSponsorVerified(true);
          } else {
            setStep('sponsor_assignment');
            return;
          }
        }
      } catch {
        setError('Unable to verify sponsor status. Please try again.');
        return;
      }
    }

    setError('');
    setSelectedSavedMethod(savedMethod);
    setIsOneClickMode(true);

    if (savedMethod.type === 'crypto' && savedMethod.network) {
      setPaymentMethod('crypto');
      setSelectedNetwork(savedMethod.network);
      setStep('crypto_payment');
    } else if (savedMethod.type === 'bank_transfer') {
      setPaymentMethod('bank_transfer');
      setStep('bank_transfer');
    }
  };

  // Sponsor username validation handler (debounced)
  const handleSponsorUsernameChange = (value: string) => {
    setSponsorUsername(value);
    if (value.trim().length === 0) {
      setSponsorValidationResult(null);
      return;
    }
    if (value.trim().length < 2) {
      setSponsorValidationResult({ isValid: false, isChecking: false, message: 'Username must be at least 2 characters', type: 'error' });
      return;
    }
    // Set checking state
    setSponsorValidationResult({ isValid: false, isChecking: true, message: 'Checking...', type: 'checking' });
    // Debounced check
    clearTimeout((window as any).__sponsorCheckTimer);
    (window as any).__sponsorCheckTimer = setTimeout(async () => {
      try {
        const response = await fetch('/api/check-duplicates', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({ sponsorUsername: value.trim() })
        });
        const data = await response.json();
        if (data.sponsorExists) {
          setSponsorValidationResult({ isValid: true, isChecking: false, message: '✅ Sponsor found', type: 'success', userId: data.sponsorUserId });
        } else {
          setSponsorValidationResult({ isValid: false, isChecking: false, message: 'Sponsor username not found', type: 'error' });
        }
      } catch {
        setSponsorValidationResult({ isValid: false, isChecking: false, message: 'Error checking sponsor', type: 'error' });
      }
    }, 500);
  };

  const handleBack = () => {
    switch (step) {
      case 'sponsor_assignment':
        clearPendingPurchaseState();
        setStep('amount');
        break;
      case 'payment_method':
        // Going back to amount step - clear any saved state
        clearPendingPurchaseState();
        setStep('amount');
        break;
      case 'crypto_network': {
        // SA users came via payment_method; non-SA users skip payment_method and
        // reach crypto_network directly from the amount step (sponsor was already
        // satisfied on the way forward), so back must return them to amount rather
        // than re-triggering the sponsor_assignment gate ("Sponsor Required" popup).
        const backCountry = user?.database_user?.country_of_residence || user?.country_of_residence || user?.country_code;
        const backIsSA = ['ZAF', 'NAM', 'SWZ', 'ZA'].includes(backCountry);
        if (backIsSA) {
          setStep('payment_method');
        } else {
          clearPendingPurchaseState();
          setStep('amount');
        }
        break;
      }
      case 'crypto_payment':
        setStep('crypto_network');
        break;
      case 'bank_transfer':
        // SA users go directly from amount->sponsor->bank_transfer, so back goes to sponsor_assignment
        setStep('sponsor_assignment');
        break;
      default:
        clearPendingPurchaseState();
        setStep('amount');
    }
  };

  // Wrapped onClose handler that clears sessionStorage
  const handleClose = () => {
    clearPendingPurchaseState();
    if (onClose) {
      onClose();
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1f2937 0%, #111827 50%, #1f2937 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '3px solid #374151',
            borderTop: '3px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px auto'
          }}></div>
          <p style={{ color: '#ffffff' }}>Loading share purchase...</p>
        </div>
      </div>
    );
  }

  // Show legal document check screen if not completed or not allowed to purchase
  // KYC is NOT required for purchasing shares - users can purchase without KYC verification
  // KYC is only required for withdrawals over $1000
  if (!legalCheckComplete || !canPurchaseShares) {
    return (
      <div className={`${styles.purchaseFlowContainer} premium-purchase-flow`} style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
        overflowY: 'auto'
      }}>
        <div className={`${styles.purchaseFlowModal} premium-purchase-modal`} style={{
          padding: '40px',
          backgroundColor: 'var(--surface)',
          color: 'var(--text)',
          position: 'relative',
          border: '1px solid var(--border)',
          boxShadow: 'var(--shadow), var(--glow)',
          minHeight: 'auto',
          height: 'auto',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          borderRadius: '16px'
        }}>
          {/* Close Button */}
          <button
            onClick={handleClose}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'none',
              border: 'none',
              color: '#ffffff',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '5px',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>

          {!legalCheckComplete ? (
            // Loading legal document check
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{
                width: '50px',
                height: '50px',
                border: '3px solid #374151',
                borderTop: '3px solid #3b82f6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px auto'
              }}></div>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>
                📋 {t('purchase.checkingRequirements', 'Checking Requirements')}
              </h2>
              <p style={{ color: '#ffffff' }}>
                Verifying legal documents...
              </p>
            </div>
          ) : !canPurchaseShares ? (
            // Legal documents not agreed to
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                backgroundColor: 'rgba(239, 68, 68, 0.2)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px auto'
              }}>
                <svg width="40" height="40" fill="none" stroke="#ef4444" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>

              <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px', color: '#ef4444' }}>
                ⚠️ {t('purchase.legalDocsRequired', 'Legal Documents Required')}
              </h2>

              <p style={{ color: '#ffffff', marginBottom: '24px', fontSize: '16px', lineHeight: '1.6' }}>
                You must read and agree to all legal documents before purchasing shares. This ensures you understand the terms and risks involved.
              </p>

              <div style={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '24px',
                textAlign: 'left'
              }}>
                <h3 style={{ color: '#ef4444', fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>
                  📋 Missing Agreements:
                </h3>
                <ul style={{ color: '#ffffff', fontSize: '14px', lineHeight: '1.6', margin: 0, paddingLeft: '20px' }}>
                  {missingAgreements.map((doc, index) => (
                    <li key={index} style={{ marginBottom: '4px' }}>
                      <strong>{doc}</strong>
                    </li>
                  ))}
                </ul>
              </div>

              <div style={{
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '24px',
                textAlign: 'left'
              }}>
                <h3 style={{ color: '#3b82f6', fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>
                  📖 What You Need to Do:
                </h3>
                <ol style={{ color: '#ffffff', fontSize: '14px', lineHeight: '1.6', margin: 0, paddingLeft: '20px' }}>
                  <li style={{ marginBottom: '8px' }}>Go to your <strong>Dashboard → Legal Documents</strong></li>
                  <li style={{ marginBottom: '8px' }}>Read each document carefully</li>
                  <li style={{ marginBottom: '8px' }}>Click "View" and then "Agree" for each document</li>
                  <li style={{ marginBottom: '8px' }}>Return here to complete your share purchase</li>
                </ol>
              </div>

              <div style={{
                backgroundColor: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <p style={{ color: '#f59e0b', fontSize: '14px', fontWeight: '600', margin: 0 }}>
                  💡 These documents contain important information about mining risks
                  and your rights as a shareholder. Please read them carefully.
                </p>
              </div>

              <button
                onClick={() => {
                  // Navigate directly to legal documents section in dashboard
                  window.location.href = '/dashboard?section=legal-documents';
                }}
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
              >
                Go to Legal Documents
              </button>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.purchaseFlowContainer} premium-purchase-flow`} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
      overflowY: 'auto'
    }}>
      <div className={`${styles.purchaseFlowModal} premium-purchase-modal`} style={{
        padding: '40px',
        backgroundColor: 'var(--surface)',
        color: 'var(--text)',
        position: 'relative',
        border: '1px solid var(--border)',
        boxShadow: 'var(--shadow), var(--glow)',
        minHeight: 'auto',
        height: 'auto',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        borderRadius: '16px'
      }}>
        {/* Scrollable Content Container */}
        <div style={{
          maxHeight: '100%',
          overflowY: 'auto',
          paddingRight: '8px',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255, 255, 255, 0.3) rgba(0, 0, 0, 0.1)'
        }}>
        {/* Close Button */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: 'none',
            borderRadius: '8px',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#ffffff',
            fontSize: '18px',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
          }}
        >
          ×
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', margin: '0 0 10px 0' }}>
            💰 {t('purchase.heading', 'Purchase Gold Shares')}
          </h1>
          {currentPhase && (
            <div style={{
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '12px',
              padding: '16px',
              marginTop: '20px'
            }}>
              <p style={{ margin: '0 0 8px 0', color: '#60a5fa', fontWeight: 'bold', fontSize: '16px' }}>
                Current Phase: {currentPhase.phase_name}
              </p>
              <p style={{ margin: 0, color: '#ffffff', fontSize: '18px', fontWeight: 'bold' }}>
                Share Price: ${currentPhase.price_per_share.toFixed(2)} USD
              </p>
              {/* DISPLAY HIDE: Total Available / Remaining / shares-sold progress
                  removed from purchase modal (owner request) — supply counters stay
                  on Share Phases / admin surfaces only. */}
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div style={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '20px',
            color: '#f87171'
          }}>
            ❌ {error}
          </div>
        )}

        {/* Step Content */}
        {step === 'amount' && (
          <EnhancedAmountStep
            amount={amount}
            setAmount={setAmount}
            shares={shares}
            setShares={setShares}
            currentPhase={currentPhase}
            calculateShares={calculateShares}
            calculateAmount={calculateAmount}
            onSubmit={handleAmountSubmit}
            quickAmounts={quickAmounts}
            quickShares={quickShares}
            realTimePrice={realTimePrice || currentPhase?.price_per_share || 0}
            savedPaymentMethods={savedPaymentMethods}
            onOneClickPurchase={handleOneClickPurchase}
            user={user}
            selectedCurrency={selectedCurrency}
            setSelectedCurrency={setSelectedCurrency}
            commissionBalance={commissionBalance}
            useCommission={useCommission}
            setUseCommission={setUseCommission}
            commissionAmountToUse={commissionAmountToUse}
            setCommissionAmountToUse={setCommissionAmountToUse}
            loadingCommission={loadingCommission}
            commissionError={commissionError}
            birthdayMode={isBirthdayMode}
            pcapMode={isPcapMode}
            pcapBreakdown={pcapBreakdown}
            pcapQtys={pcapQtys}
            setPcapLineQty={setPcapLineQty}
            applyPcapLayout={applyPcapLayout}
            pcapAdvice={pcapAdvice}
            birthdayPackage={resolvedBirthdayPackage}
            setBirthdayPackage={selectBirthdayPackage}
            clearBirthdayPackage={clearBirthdayPackage}
            birthdayQty={birthdayQty}
            setBirthdayQty={setBirthdayQty}
            birthdayBreakdown={birthdayBreakdown}
          />
        )}

        {step === 'sponsor_assignment' && (
          <div style={{ textAlign: 'center' }}>
            {sponsorAssigning && (
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center',
                justifyContent: 'center', zIndex: 10, borderRadius: '16px'
              }}>
                <div style={{ color: '#fff', fontSize: '18px' }}>⏳ Assigning sponsor...</div>
              </div>
            )}

            <div style={{
              backgroundColor: 'rgba(245, 158, 11, 0.15)',
              border: '1px solid rgba(245, 158, 11, 0.4)',
              borderRadius: '12px', padding: '20px', marginBottom: '24px'
            }}>
              <h2 style={{ color: '#f59e0b', fontSize: '22px', fontWeight: 'bold', margin: '0 0 12px 0' }}>
                ⚠️ {t('purchase.sponsorRequired', 'Sponsor Required')}
              </h2>
              <p style={{ color: '#ffffff', fontSize: '14px', margin: 0, lineHeight: '1.6' }}>
                You need an active sponsor before you can purchase shares. Please enter the username of the
                person who referred you to Aureus Africa.
              </p>
            </div>

            <div style={{ textAlign: 'left', marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#ffffff', fontSize: '14px', marginBottom: '8px', fontWeight: '600' }}>
                Sponsor Username
              </label>
              <input
                type="text"
                value={sponsorUsername}
                onChange={(e) => handleSponsorUsernameChange(e.target.value)}
                placeholder="Enter your sponsor's username"
                style={{
                  width: '100%', padding: '14px 16px', borderRadius: '10px', fontSize: '16px',
                  backgroundColor: 'rgba(255,255,255,0.05)', color: '#ffffff',
                  border: sponsorValidationResult
                    ? sponsorValidationResult.isValid ? '2px solid #10b981' : sponsorValidationResult.isChecking ? '2px solid #3b82f6' : '2px solid #ef4444'
                    : '1px solid rgba(255,255,255,0.2)',
                  outline: 'none', boxSizing: 'border-box'
                }}
              />
              {sponsorValidationResult && (
                <p style={{
                  margin: '8px 0 0 0', fontSize: '13px', fontWeight: '500',
                  color: sponsorValidationResult.isChecking ? '#3b82f6' : sponsorValidationResult.isValid ? '#10b981' : '#ef4444'
                }}>
                  {sponsorValidationResult.isChecking ? '🔍 ' : ''}{sponsorValidationResult.message}
                </p>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleBack}
                style={{
                  flex: 1, padding: '14px', borderRadius: '10px', fontSize: '16px', fontWeight: '600',
                  backgroundColor: 'rgba(255,255,255,0.1)', color: '#ffffff', border: '1px solid rgba(255,255,255,0.2)',
                  cursor: 'pointer'
                }}
              >
                ← Back
              </button>
              <button
                onClick={handleSponsorAssignment}
                disabled={!sponsorValidationResult?.isValid || sponsorValidationResult?.isChecking || sponsorAssigning}
                style={{
                  flex: 2, padding: '14px', borderRadius: '10px', fontSize: '16px', fontWeight: '600',
                  backgroundColor: sponsorValidationResult?.isValid ? '#10b981' : 'rgba(255,255,255,0.1)',
                  color: '#ffffff', border: 'none',
                  cursor: sponsorValidationResult?.isValid ? 'pointer' : 'not-allowed',
                  opacity: sponsorValidationResult?.isValid ? 1 : 0.5
                }}
              >
                Confirm Sponsor & Continue →
              </button>
            </div>
          </div>
        )}

        {step === 'payment_method' && (
          <PaymentMethodStep
            amount={amount}
            shares={parseInt(shares) || 0}
            currentPhase={currentPhase}
            onSelect={handlePaymentMethodSelect}
            onBack={handleBack}
            productionCapitalPackages={isPcapMode ? pcapLines : undefined}
            birthdayPackage={isBirthdayMode ? resolvedBirthdayPackage : undefined}
            birthdayQty={isBirthdayMode ? birthdayQty : undefined}
            tierBonusShares={calculateBonusShares(parseInt(shares) || 0)}
            singleShareActive={isSingleShareSpecialActive() && !isPcapMode && !isBirthdayMode}
          />
        )}

        {step === 'crypto_network' && (
          <CryptoNetworkStep
            networks={paymentNetworks}
            onSelect={handleNetworkSelect}
            onBack={handleBack}
          />
        )}

        {step === 'crypto_payment' && selectedNetwork && (
          <CryptoPaymentStep
            amount={amount}
            shares={parseInt(shares) || 0}
            network={selectedNetwork}
            currentPhase={currentPhase}
            user={user}
            onBack={handleBack}
            onComplete={() => { clearPendingPurchaseState(); setStep('pending'); }}
            useCommission={useCommission}
            commissionBalance={commissionBalance}
            commissionAmountToUse={commissionAmountToUse}
            bonusShares={calculateBonusShares(parseInt(shares) || 0)}
            totalShares={calculateTotalShares(parseInt(shares) || 0)}
            existingPaymentId={resumePayment?.paymentId}
            isResubmit={resumePayment?.isResubmit}
            isBalancePayment={resumePayment?.isBalancePayment}
            originalPaymentId={resumePayment?.originalPaymentId}
            balanceOwed={resumePayment?.balanceOwed}
            birthdayPackage={isBirthdayMode ? resolvedBirthdayPackage : undefined}
            birthdayQty={isBirthdayMode ? birthdayQty : undefined}
            productionCapitalPackage={isPcapMode && pcapLines[0] ? pcapLines[0].packageId : undefined}
            productionCapitalPackageQty={isPcapMode && pcapLines[0] ? pcapLines[0].qty : undefined}
            productionCapitalPackages={isPcapMode ? pcapLines : undefined}
            singleShareActive={isSingleShareSpecialActive() && !isPcapMode && !isBirthdayMode}
          />
        )}

        {step === 'bank_transfer' && (
          <BankTransferStep
            amount={amount}
            shares={parseInt(shares) || 0}
            currentPhase={currentPhase}
            user={user}
            onBack={handleBack}
            onComplete={() => { clearPendingPurchaseState(); setStep('pending'); }}
            bonusShares={calculateBonusShares(parseInt(shares) || 0)}
            totalShares={calculateTotalShares(parseInt(shares) || 0)}
            existingPaymentId={resumePayment?.paymentId}
            isResubmit={resumePayment?.isResubmit}
            isBalancePayment={resumePayment?.isBalancePayment}
            originalPaymentId={resumePayment?.originalPaymentId}
            balanceOwed={resumePayment?.balanceOwed}
            birthdayPackage={isBirthdayMode ? resolvedBirthdayPackage : undefined}
            birthdayQty={isBirthdayMode ? birthdayQty : undefined}
            productionCapitalPackage={isPcapMode && pcapLines[0] ? pcapLines[0].packageId : undefined}
            productionCapitalPackageQty={isPcapMode && pcapLines[0] ? pcapLines[0].qty : undefined}
            productionCapitalPackages={isPcapMode ? pcapLines : undefined}
            singleShareActive={isSingleShareSpecialActive() && !isPcapMode && !isBirthdayMode}
          />
        )}

        {/* Yoco payment step removed (v4.3.61) */}

        {step === 'pending' && (
          <PendingStep
            onClose={onClose}
            user={user}
            onPurchaseComplete={onPurchaseComplete}
            fullyCoveredByCommission={purchaseFullyCovered}
          />
        )}
        </div> {/* End Scrollable Content Container */}
      </div>
    </div>
  );
};

// Payment Method Selection Step
const PaymentMethodStep: React.FC<{
  amount: string;
  shares: number;
  currentPhase: CurrentPhase | null;
  onSelect: (method: 'crypto' | 'bank_transfer') => void;
  onBack: () => void;
  productionCapitalPackages?: Array<{ packageId: ProductionCapitalPackageId; qty: number }>;
  birthdayPackage?: BirthdayPackageId;
  birthdayQty?: number;
  tierBonusShares?: number;
  singleShareActive?: boolean;
}> = ({
  amount: _amount,
  shares,
  currentPhase,
  onSelect,
  onBack,
  productionCapitalPackages,
  birthdayPackage,
  birthdayQty,
  tierBonusShares = 0,
  singleShareActive = false,
}) => {
  const { t } = useTranslation('common');
  const totalCost = shares * (currentPhase?.price_per_share || 0);

  return (
    <div>
      <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px', textAlign: 'center' }}>
        💳 {t('purchase.selectPaymentMethod', 'Select Payment Method')}
      </h2>

      {/* Purchase Summary */}
      <div style={{
        backgroundColor: 'rgba(55, 65, 81, 0.5)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '30px'
      }}>
        <h3 style={{ color: '#fbbf24', fontWeight: 'bold', margin: '0 0 12px 0' }}>
          📋 PURCHASE SUMMARY:
        </h3>
        <div style={{ color: '#ffffff', fontSize: '14px' }}>
          <p style={{ margin: '0 0 8px 0' }}>Amount: ${totalCost.toFixed(2)} USD</p>
          <p style={{ margin: '0 0 8px 0' }}>Shares: {shares}</p>
          <PurchaseRewardPreview
            productionCapitalPackages={productionCapitalPackages}
            birthdayPackage={birthdayPackage}
            birthdayQty={birthdayQty}
            phasePrice={currentPhase?.price_per_share}
            paidShares={shares}
            tierBonusShares={tierBonusShares}
            singleShareActive={singleShareActive}
          />
          <p style={{ margin: '8px 0' }}>Price per Share: ${currentPhase?.price_per_share.toFixed(2)}</p>
          <p style={{ margin: 0 }}>Phase: {currentPhase?.phase_name}</p>
        </div>
      </div>

      {/* Payment Options */}
      <div style={{ marginBottom: '30px' }}>
        {/* USDT Crypto Payment */}
        <button
          onClick={() => onSelect('crypto')}
          style={{
            width: '100%',
            padding: '20px',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            border: '2px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '12px',
            color: 'white',
            marginBottom: '16px',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ textAlign: 'left' }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 'bold' }}>
                💎 USDT Payment (Recommended)
              </h3>
              <p style={{ margin: 0, color: '#ffffff', fontSize: '14px' }}>
                Fast processing • Multiple networks • Instant confirmation
              </p>
            </div>
            <div style={{ fontSize: '24px' }}>→</div>
          </div>
        </button>

        {/* Bank Transfer */}
        <button
          onClick={() => onSelect('bank_transfer')}
          style={{
            width: '100%',
            padding: '20px',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            border: '2px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '12px',
            color: 'white',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ textAlign: 'left' }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 'bold' }}>
                🏦 Bank Transfer (ZAR)
              </h3>
              <p style={{ margin: 0, color: '#ffffff', fontSize: '14px' }}>
                South Africa, Eswatini, Namibia • Request instant clearance for faster processing
              </p>
            </div>
            <div style={{ fontSize: '24px' }}>→</div>
          </div>
        </button>
      </div>

      {/* Back Button */}
      <button
        onClick={onBack}
        style={{
          width: '100%',
          padding: '12px',
          backgroundColor: 'transparent',
          border: '2px solid #374151',
          borderRadius: '8px',
          color: '#ffffff',
          fontSize: '16px',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
      >
        ← Back to Amount
      </button>
    </div>
  );
};

// Crypto Network Selection Step
const CryptoNetworkStep: React.FC<{
  networks: PaymentNetwork[];
  onSelect: (network: PaymentNetwork) => void;
  onBack: () => void;
}> = ({ networks, onSelect, onBack }) => {
  const { t } = useTranslation('common');
  return (
    <div>
      <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px', textAlign: 'center' }}>
        🌐 {t('purchase.selectUsdtNetwork', 'Select USDT Network')}
      </h2>

      <p style={{ color: '#ffffff', marginBottom: '30px', textAlign: 'center' }}>
        Choose your preferred USDT network for payment:
      </p>

      {/* Network Options */}
      <div style={{ marginBottom: '30px' }}>
        {networks.length === 0 ? (
          <div style={{
            padding: '40px 20px',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            border: '2px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h3 style={{ color: '#ef4444', fontSize: '18px', fontWeight: 'bold', margin: '0 0 12px 0' }}>
              No Payment Networks Available
            </h3>
            <p style={{ color: '#ffffff', fontSize: '14px', margin: '0 0 16px 0' }}>
              Payment networks are currently being configured. Please contact support or try again later.
            </p>
            <p style={{ color: '#ffffff', fontSize: '13px', margin: 0 }}>
              Admin: Add wallet addresses via Admin Panel → Wallet Manager
            </p>
          </div>
        ) : (
          networks.map((network) => (
            <button
              key={network.id}
              onClick={() => onSelect(network)}
              style={{
                width: '100%',
                padding: '20px',
                backgroundColor: 'rgba(31, 41, 55, 0.8)',
                border: '2px solid #374151',
                borderRadius: '12px',
                color: 'white',
                marginBottom: '16px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <span style={{ fontSize: '24px' }}>{network.icon}</span>
                  <div style={{ textAlign: 'left' }}>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 'bold' }}>
                      {network.name}
                    </h3>
                    <p style={{ margin: 0, color: '#ffffff', fontSize: '14px' }}>
                      USDT-{network.technical}
                    </p>
                  </div>
                </div>
                <div style={{ fontSize: '20px' }}>→</div>
              </div>
            </button>
          ))
        )}
      </div>

      {/* Regulatory Notice (2026-09-13, per JP — SA crypto regulation change) */}
      <div style={{
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        border: '1px solid rgba(245, 158, 11, 0.3)',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '20px'
      }}>
        <h3 style={{ color: '#fbbf24', fontWeight: 'bold', margin: '0 0 12px 0' }}>
          ⚠️ Important Update: Crypto Payments and Withdrawals
        </h3>
        <p style={{ color: '#ffffff', fontSize: '14px', margin: '0 0 10px 0' }}>
          Following changes to South African cryptocurrency regulations, we will no longer support peer-to-peer exchanges such as Remitano.
        </p>
        <p style={{ color: '#ffffff', fontSize: '14px', margin: '0 0 10px 0' }}>
          All previously saved withdrawal wallet addresses have been removed. Please add a new compatible wallet address before requesting your next withdrawal.
        </p>
        <p style={{ color: '#ffffff', fontSize: '14px', margin: 0 }}>
          To keep transaction costs low, we will now accept payments <strong style={{ color: '#fbbf24' }}>only in USDT on BNB Smart Chain (BSC/BEP-20)</strong>. Please ensure you select the correct network when sending funds.
        </p>
      </div>

      {/* Back Button */}
      <button
        onClick={onBack}
        style={{
          width: '100%',
          padding: '12px',
          backgroundColor: 'transparent',
          border: '2px solid #374151',
          borderRadius: '8px',
          color: '#ffffff',
          fontSize: '16px',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
      >
        ← Back to Payment Method
      </button>
    </div>
  );
};

// Pending Payment Step
const PendingStep: React.FC<{
  onClose?: () => void;
  user?: any;
  onPurchaseComplete?: (isFirstPurchase: boolean) => void;
  fullyCoveredByCommission?: boolean;
}> = ({ onClose, user, onPurchaseComplete, fullyCoveredByCommission = false }) => {
  const { t } = useTranslation('common');
  const [, setIsCheckingFirstPurchase] = useState(false);
  const [isFirstPurchase, setIsFirstPurchase] = useState(false);

  useEffect(() => {
    const checkFirstPurchase = async () => {
      if (!user) return;

      try {
        setIsCheckingFirstPurchase(true);

        // Extract numeric user ID using inline parsing (PendingStep is a separate component)
        const parseUserId = (id: any): number | null => {
          if (!id) return null;
          if (typeof id === 'number') return id > 0 ? id : null;
          const s = String(id);
          if (s.includes('db_')) return parseInt(s.replace(/db_/g, ''), 10) || null;
          const n = parseInt(s, 10);
          return !isNaN(n) && n > 0 ? n : null;
        };
        const userId = parseUserId(user?.database_user?.id)
          || parseUserId(user?.user_metadata?.user_id)
          || parseUserId(user?.id);
        if (!userId) {
          console.warn('⚠️ Cannot check first purchase: no numeric user ID');
          return;
        }

        // Use API endpoint to check purchase history (bypasses RLS server-side)
        const response = await fetch('/api/purchase/check-first-purchase', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
          body: JSON.stringify({ userId })
        });

        if (!response.ok) {
          console.error('Error checking purchase history:', response.status);
          return;
        }

        const result = await response.json();

        if (!result.success) {
          console.error('Error checking purchase history:', result.error);
          return;
        }

        console.log('🔍 First purchase check for user', userId, ':', result.purchaseCount, 'active purchases');

        // If this is the first purchase (only 1 purchase exists - the one just made)
        const firstPurchase = result.isFirstPurchase;
        setIsFirstPurchase(firstPurchase);

        // Notify parent component
        if (firstPurchase && onPurchaseComplete) {
          console.log('🎯 First purchase confirmed, notifying parent component');
          onPurchaseComplete(true);
        }
      } catch (error) {
        console.error('Error in checkFirstPurchase:', error);
      } finally {
        setIsCheckingFirstPurchase(false);
      }
    };

    checkFirstPurchase();
  }, [user, onPurchaseComplete]);

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '80px',
        height: '80px',
        backgroundColor: 'rgba(245, 158, 11, 0.2)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px auto'
      }}>
        <svg width="40" height="40" fill="none" stroke="#f59e0b" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>

      <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: '0 0 16px 0' }}>
        {fullyCoveredByCommission ? `🎉 ${t('purchase.completedHeading', 'Purchase Completed!')}` : `🎉 ${t('purchase.submittedHeading', 'Payment Submitted Successfully!')}`}
      </h2>

      <p style={{ color: '#ffffff', marginBottom: '24px', fontSize: '16px' }}>
        {fullyCoveredByCommission
          ? 'Your purchase was fully covered by your commission balance! Shares have been added to your account.'
          : 'Your payment has been submitted for admin review. You will receive a confirmation once approved.'
        }
      </p>

      {fullyCoveredByCommission && (
        <div style={{
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          border: '2px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <h3 style={{ color: '#10b981', fontWeight: 'bold', margin: '0 0 12px 0' }}>
            💰 Commission Balance Used
          </h3>
          <p style={{ color: '#ffffff', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
            Your commission balance was successfully applied to this purchase. No additional payment required!
          </p>
        </div>
      )}

      {isFirstPurchase && (
        <div style={{
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <h3 style={{ color: '#22c55e', fontWeight: 'bold', margin: '0 0 12px 0' }}>
            🎊 Welcome to Aureus Alliance!
          </h3>
          <p style={{ color: '#ffffff', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
            This is your first share purchase! Please complete KYC verification to unlock all features including referral commissions and withdrawals.
          </p>
        </div>
      )}

      <div style={{
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        border: '1px solid rgba(245, 158, 11, 0.3)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <h3 style={{ color: '#fbbf24', fontWeight: 'bold', margin: '0 0 12px 0' }}>
          ⏱️ PROCESSING TIMES:
        </h3>
        <div style={{ color: '#ffffff', fontSize: '14px', textAlign: 'left' }}>
          <p style={{ margin: '0 0 8px 0' }}>• Crypto payments: 1-24 hours</p>
          <p style={{ margin: 0 }}>• Bank transfers: Same day (FNB) or 1-5 days (other banks - request instant clearance)</p>
        </div>
      </div>

      <div style={{
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <h3 style={{ color: '#60a5fa', fontWeight: 'bold', margin: '0 0 12px 0' }}>
          📧 WHAT'S NEXT:
        </h3>
        <div style={{ color: '#ffffff', fontSize: '14px', textAlign: 'left' }}>
          <p style={{ margin: '0 0 8px 0' }}>• Admin will review your payment proof</p>
          <p style={{ margin: '0 0 8px 0' }}>• You'll receive email confirmation when approved</p>
          <p style={{ margin: '0 0 8px 0' }}>• Shares will be added to your account</p>
          <p style={{ margin: 0 }}>• You can make additional purchases anytime</p>
        </div>
      </div>

      <button
        onClick={onClose}
        style={{
          width: '100%',
          padding: '16px',
          backgroundColor: '#3b82f6',
          border: 'none',
          borderRadius: '12px',
          color: 'white',
          fontSize: '18px',
          fontWeight: 'bold',
          cursor: 'pointer',
          marginBottom: '12px'
        }}
      >
        Return to Dashboard
      </button>

      <button
        onClick={onClose}
        style={{
          width: '100%',
          padding: '12px',
          backgroundColor: 'transparent',
          border: '2px solid #374151',
          borderRadius: '8px',
          color: '#ffffff',
          fontSize: '16px',
          cursor: 'pointer'
        }}
      >
        Close
      </button>
    </div>
  );
};

// Enhanced Amount Step Component with Quick Amounts and One-Click Purchase
const EnhancedAmountStep: React.FC<{
  amount: string;
  setAmount: (value: string) => void;
  shares: string;
  setShares: (value: string) => void;
  currentPhase: CurrentPhase | null;
  calculateShares: (amount: string) => number;
  calculateAmount: (shares: string) => number;
  onSubmit: () => void;
  quickAmounts: number[];
  quickShares: number[];
  realTimePrice: number;
  savedPaymentMethods: SavedPaymentMethod[];
  onOneClickPurchase: (method: SavedPaymentMethod) => void;
  user?: any;
  selectedCurrency: 'USD' | 'ZAR';
  setSelectedCurrency: (currency: 'USD' | 'ZAR') => void;
  commissionBalance: number;
  useCommission: boolean;
  setUseCommission: (value: boolean) => void;
  commissionAmountToUse: string;
  setCommissionAmountToUse: (value: string) => void;
  loadingCommission: boolean;
  commissionError: string;
  birthdayMode: boolean;
  pcapMode?: boolean;
  pcapBreakdown?: { packageId: string; name: string; qty?: number; paidShares: number; priceUsd: number; investorBonus: number; investorTotal: number; sponsorBonus: number } | null;
  pcapQtys?: Record<ProductionCapitalPackageId, number>;
  setPcapLineQty?: (packageId: ProductionCapitalPackageId, qty: number) => void;
  applyPcapLayout?: (lines: Array<{ packageId: ProductionCapitalPackageId; qty: number }>) => void;
  pcapAdvice?: { better: { lines: Array<{ packageId: string; name: string; qty: number; investorTotal: number; sponsorBonus: number; costUsd: number }>; unspentUsd?: number } | null; buyerLoss: number; sponsorLoss: number } | null;
  birthdayPackage: BirthdayPackageId | null;
  setBirthdayPackage: (packageId: BirthdayPackageId) => void;
  clearBirthdayPackage: () => void;
  birthdayQty: number;
  setBirthdayQty: (value: number) => void;
  birthdayBreakdown: { packageId: BirthdayPackageId; qty: number; baseShares: number; buyerBonus: number; uplineBonus: number } | null;
}> = ({
  amount,
  setAmount,
  shares,
  setShares,
  currentPhase,
  calculateShares: _calculateShares,
  calculateAmount,
  onSubmit,
  quickAmounts: _quickAmounts,
  quickShares,
  realTimePrice,
  savedPaymentMethods,
  onOneClickPurchase,
  user,
  selectedCurrency,
  setSelectedCurrency,
  commissionBalance: _commissionBalance,
  useCommission: _useCommission,
  setUseCommission: _setUseCommission,
  commissionAmountToUse: _commissionAmountToUse,
  setCommissionAmountToUse: _setCommissionAmountToUse,
  loadingCommission: _loadingCommission,
  commissionError: _commissionError,
  birthdayMode,
  pcapMode = false,
  pcapBreakdown = null,
  pcapQtys = { P1: 0, P2: 0, P3: 0, P4: 0, P5: 0 },
  setPcapLineQty,
  applyPcapLayout,
  pcapAdvice = null,
  birthdayPackage,
  setBirthdayPackage,
  clearBirthdayPackage,
  birthdayQty,
  setBirthdayQty,
  birthdayBreakdown
  }) => {
  const [pcapSpendInput, setPcapSpendInput] = useState('');
  const [pcapSpendNote, setPcapSpendNote] = useState('');
  const formatShareCount = (value: number) => String(Number(value.toFixed(4))).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
  // Calculate shares from the shares input (now using share-based purchasing)
  const numShares = parseInt(shares) || 0;
  const bonusShares = calculateBonusShares(numShares);
  const totalSharesWithBonus = calculateTotalShares(numShares);

  // Extract user ID for special pricing using centralized utility
  const numericUserId = extractNumericId(user?.database_user?.id)
    || extractNumericId(user?.user_metadata?.user_id)
    || extractNumericId(user?.id);

  const isSpecialPricingUser = numericUserId === TEST_USER_ID;
  const isSpecialPricingActive = isSpecialPricingUser && numShares === 1;

  // Single-Share Special (display-only): when active, the quick-select grid is
  // hidden and a promo notice is shown instead. Self-reverts after the window
  // (endDate) with no code change. Does NOT alter pricing, minting, commission,
  // or the server-side bonus logic (minting is date-gated + env-gated server-side).
  const singleShareSpecialActive = isSingleShareSpecialActive();

  // DISPLAY-ONLY Single-Share Special bonus for the Purchase Preview. The buyer's
  // +1-per-paid-share BOGO bonus is granted SERVER-side as a separate gift row
  // (lib/singleShareGiftIssuance.js), NOT via the base-purchase payload — so this
  // value MUST NOT be folded into the `bonusShares`/`totalShares` PROPS passed to
  // CryptoPaymentStep/BankTransferStep (those feed shares_purchased/sharesToAdd and
  // would double-grant). It is used ONLY inside the preview JSX below.
  const ssSpecialBuyerBonus = (singleShareSpecialActive && !birthdayMode)
    ? (computeSingleShareSpecialBreakdown(numShares)?.buyerBonus ?? 0)
    : 0;
  const displayBonusShares = bonusShares + ssSpecialBuyerBonus;
  const displayTotalShares = totalSharesWithBonus + ssSpecialBuyerBonus;

  // Calculate total cost with special pricing check
  const normalCost = numShares * (realTimePrice || currentPhase?.price_per_share || 0);
  const totalCost = isSpecialPricingActive ? TEST_PRICE_SINGLE_SHARE : normalCost;

  // Bank transfer configuration (same as BankTransferStep)
  const bankTransferConfig = {
    exchangeRate: 17, // USD to ZAR
    transactionFee: 0.10 // 10% transaction fee
  };

  // Check if user is from South Africa, Namibia, or Eswatini
  const userCountry = user?.database_user?.country_of_residence || user?.country_of_residence || user?.country_code;
  const isSouthernAfricaUser = ['ZAF', 'NAM', 'SWZ', 'ZA'].includes(userCountry);

  // v4.3.61: Yoco removed
  const isYocoTestUser = false;

  // Debug logging
  console.log('🌍 User country detection:', {
    user: user,
    userCountry: userCountry,
    isSouthernAfricaUser: isSouthernAfricaUser,
    database_user: user?.database_user,
    country_of_residence: user?.database_user?.country_of_residence,
    userId: numericUserId,
    isYocoTestUser: isYocoTestUser,
    isSpecialPricingActive: isSpecialPricingActive,
    totalCost: totalCost
  });

  // Calculate ZAR amounts for bank transfer
  const zarAmount = totalCost * bankTransferConfig.exchangeRate;
  const feeAmount = zarAmount * bankTransferConfig.transactionFee;
  const totalZarAmount = zarAmount + feeAmount;

  return (
    <div>
      <h2 style={{
        fontSize: '24px',
        fontWeight: 'bold',
        marginBottom: '8px',
        textAlign: 'center',
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent'
      }}>
        🚀 Purchase Shares
      </h2>

      <p style={{ color: '#ffffff', marginBottom: '24px', textAlign: 'center', fontSize: '16px' }}>
        Choose your share purchase amount with our streamlined interface
      </p>

      {/* Payment Method Selection for Southern Africa Users */}
      {isSouthernAfricaUser && (
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            color: '#ffffff',
            marginBottom: '12px',
            fontSize: '16px',
            fontWeight: '600',
            textAlign: 'center'
          }}>
            💳 Payment Method & Currency
          </label>
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            marginBottom: '16px'
          }}>
            <button
              onClick={() => setSelectedCurrency('USD')}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: selectedCurrency === 'USD' ? '2px solid #f59e0b' : '2px solid #374151',
                backgroundColor: selectedCurrency === 'USD' ? '#1f2937' : '#111827',
                color: selectedCurrency === 'USD' ? '#f59e0b' : '#9ca3af',
                fontWeight: selectedCurrency === 'USD' ? 'bold' : 'normal',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              💰 USDT Payment (USD)
            </button>
            <button
              onClick={() => {
                setSelectedCurrency('ZAR');
              }}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: selectedCurrency === 'ZAR' ? '2px solid #f59e0b' : '2px solid #374151',
                backgroundColor: selectedCurrency === 'ZAR' ? '#1f2937' : '#111827',
                color: selectedCurrency === 'ZAR' ? '#f59e0b' : '#9ca3af',
                fontWeight: selectedCurrency === 'ZAR' ? 'bold' : 'normal',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              🏦 Bank Transfer (ZAR)
            </button>
          </div>
          {selectedCurrency === 'ZAR' && (
            <div style={{
              backgroundColor: '#1f2937',
              border: '1px solid #374151',
              borderRadius: '8px',
              padding: '12px',
              textAlign: 'center'
            }}>
              <div style={{ color: '#f59e0b', fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
                💱 Exchange Rate: R{bankTransferConfig.exchangeRate} per $1 USD + 10% fee
              </div>
              <div style={{ color: '#ffffff', fontSize: '13px' }}>
                All amounts will be converted to ZAR for bank transfer
              </div>
            </div>
          )}
        </div>
      )}

      {/* Real-time Price Display */}
      <div className={styles.priceDisplay} style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <span style={{ fontSize: '20px' }}>📈</span>
          <div>
            <div style={{ color: '#10b981', fontSize: '18px', fontWeight: 'bold' }}>
              {selectedCurrency === 'ZAR' ?
                `R${(realTimePrice * bankTransferConfig.exchangeRate * (1 + bankTransferConfig.transactionFee)).toFixed(2)} per share` :
                `$${realTimePrice.toFixed(2)} per share`
              }
            </div>
            <div style={{ color: '#ffffff', fontSize: '13px' }}>
              {currentPhase?.phase_name} • Live Price
              {selectedCurrency === 'ZAR' && ' (incl. exchange rate + 10% fee)'}
            </div>
          </div>
        </div>
      </div>

      {/* Production Capital: build a layout from one or more packages */}
      {pcapMode && (
        <div style={{ marginBottom: '16px', padding: '14px', border: '2px solid #D4AF37', borderRadius: '10px', background: 'rgba(212,175,55,0.08)' }}>
          <div style={{ color: '#FFD700', fontWeight: 700, marginBottom: '6px' }}>
            Production Capital Share Campaign
          </div>
          <div style={{ color: '#D4AF37', fontSize: '12px', marginBottom: '10px' }}>
            Set a quantity on each package you want. You can buy more than one package in the same order.
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '8px' }}>
            <input
              type="number"
              min={selectedCurrency === 'ZAR' ? 1870 : 100}
              step="1"
              value={pcapSpendInput}
              onChange={(event) => { setPcapSpendInput(event.target.value); setPcapSpendNote(''); }}
              placeholder={selectedCurrency === 'ZAR' ? 'Amount in ZAR, minimum R1,870' : 'Amount in USD, minimum $100'}
              style={{ flex: '1 1 180px', background: '#121212', color: '#fff', border: '1px solid #D4AF37', borderRadius: '8px', padding: '10px 12px' }}
            />
            <button
              type="button"
              onClick={() => {
                const entered = Number(pcapSpendInput);
                const usd = selectedCurrency === 'ZAR' ? entered / (17 * 1.1) : entered;
                if (!Number.isFinite(usd) || usd < 100) {
                  setPcapSpendNote(selectedCurrency === 'ZAR'
                    ? 'Enter at least R1,870, the bank-transfer cost of $100.'
                    : 'Enter at least $100.');
                  return;
                }
                const best = optimizeProductionCapitalSpend(usd);
                if (!best) {
                  setPcapSpendNote('That amount cannot buy a package.');
                  return;
                }
                applyPcapLayout?.(best.lines.map((line) => ({ packageId: line.packageId as ProductionCapitalPackageId, qty: line.qty })));
                const unspent = best.unspentUsd > 0.5
                  ? ` $${best.unspentUsd.toFixed(2)} is not used because packages are sold in whole units.`
                  : '';
                setPcapSpendNote(`Best layout selected.${unspent}`);
              }}
              style={{ background: '#D4AF37', color: '#1a1a1a', border: 'none', borderRadius: '8px', padding: '10px 14px', fontWeight: 700, cursor: 'pointer' }}
            >
              Build best packages
            </button>
          </div>
          {pcapSpendNote && (
            <div style={{ color: '#fbbf24', fontSize: '12px', marginBottom: '10px' }}>{pcapSpendNote}</div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '12px' }}>
            {listActiveProductionCapitalPackages().map((pkg) => {
              const qty = pcapQtys[pkg.id] || 0;
              const b = computeProductionCapitalBreakdown(pkg.id, Math.max(1, qty))!;
              const max = PRODUCTION_CAPITAL_CAMPAIGN.maxPackageQty || 100;
              return (
                <div
                  key={pkg.id}
                  style={{
                    textAlign: 'left',
                    borderRadius: '10px',
                    padding: '12px',
                    background: qty > 0 ? 'rgba(212,175,55,0.25)' : '#121212',
                    color: '#fff',
                    border: qty > 0 ? '2px solid #FFD700' : '2px solid #D4AF37',
                  }}
                >
                  <div style={{ color: '#FFD700', fontWeight: 700, fontSize: '14px', marginBottom: '4px' }}>{pkg.name}</div>
                  <div style={{ fontSize: '12px', lineHeight: 1.45, color: '#eee', marginBottom: '8px' }}>
                    <div>Pay <strong style={{ color: '#FFD700' }}>${b.priceUsd.toLocaleString()}</strong></div>
                    <div>Paid: <strong>{b.paidShares}</strong></div>
                    <div>Your bonus shares: <strong style={{ color: '#10b981' }}>+{formatShareCount(b.investorBonus)}</strong></div>
                    <div>You get: <strong>{formatShareCount(b.investorTotal)}</strong></div>
                    <div>Your sponsor receives: <strong style={{ color: '#10b981' }}>+{formatShareCount(b.sponsorBonus)}</strong></div>
                    <div style={{ color: '#fbbf24' }}>{qty > 0 ? `For the ×${qty} you chose` : 'For 1 package'}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button type="button" onClick={() => setPcapLineQty?.(pkg.id, qty - 1)} disabled={qty <= 0} style={{ width: '32px', height: '32px', borderRadius: '8px', border: '1px solid #D4AF37', background: qty <= 0 ? '#2a2a2a' : '#D4AF37', color: qty <= 0 ? '#888' : '#1a1a1a', fontWeight: 'bold', cursor: qty <= 0 ? 'not-allowed' : 'pointer' }}>−</button>
                    <div style={{ minWidth: '28px', textAlign: 'center', fontWeight: 700 }}>×{qty}</div>
                    <button type="button" onClick={() => setPcapLineQty?.(pkg.id, qty + 1)} disabled={qty >= max} style={{ width: '32px', height: '32px', borderRadius: '8px', border: '1px solid #D4AF37', background: qty >= max ? '#2a2a2a' : '#D4AF37', color: qty >= max ? '#888' : '#1a1a1a', fontWeight: 'bold', cursor: qty >= max ? 'not-allowed' : 'pointer' }}>+</button>
                  </div>
                </div>
              );
            })}
          </div>
          {pcapAdvice?.better && (
            <div style={{ background: 'rgba(245, 158, 11, 0.12)', border: '1px solid #f59e0b', borderRadius: '8px', padding: '10px', marginBottom: '10px', color: '#fde68a', fontSize: '13px', lineHeight: 1.45 }}>
              <div>
                This layout gives you {formatShareCount(pcapAdvice.buyerLoss)} fewer shares than {pcapAdvice.better.lines.map((line) => `${line.name} ×${line.qty}`).join(' + ')} for the same ${pcapAdvice.better.costUsd.toLocaleString()}.
                {pcapAdvice.sponsorLoss > 0
                  ? ` Your sponsor also receives ${formatShareCount(pcapAdvice.sponsorLoss)} fewer shares.`
                  : ' Your sponsor bonus is the same either way.'}
              </div>
              <button
                type="button"
                onClick={() => applyPcapLayout?.(pcapAdvice.better!.lines.map((line) => ({ packageId: line.packageId as ProductionCapitalPackageId, qty: line.qty })))}
                style={{ marginTop: '8px', background: '#f59e0b', color: '#1a1a1a', border: 'none', borderRadius: '8px', padding: '8px 12px', fontWeight: 700, cursor: 'pointer' }}
              >
                Use the better layout
              </button>
            </div>
          )}
          <PurchaseRewardPreview
            productionCapitalPackages={(['P1', 'P2', 'P3', 'P4', 'P5'] as ProductionCapitalPackageId[])
              .map((packageId) => ({ packageId, qty: pcapQtys[packageId] || 0 }))
              .filter((line) => line.qty > 0)}
            phasePrice={realTimePrice || currentPhase?.price_per_share}
            paidShares={numShares}
          />
        </div>
      )}

      {birthdayMode && birthdayPackage && birthdayBreakdown && (
        <div style={{
          marginBottom: '24px',
          padding: '18px',
          borderRadius: '12px',
          background: '#1a1a1a',
          border: '2px solid #d4af37'
        }}>
          <div style={{ color: '#d4af37', fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>
            🎉 {BIRTHDAY_PROMO_CONFIG.packages[birthdayPackage].label} — Aureus Anniversary Package
          </div>
          {/* In-flow package selector: let the buyer switch between the two
              birthday packages — and, while the Single-Share Special is active,
              switch OUT to the single-share special path — without leaving the
              purchase screen. */}
          <div style={{ color: '#ffffff', fontSize: '13px', marginBottom: '8px' }}>
            Choose your package:
          </div>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
            {/* First choice: Single Share Special (BOGO). Selecting it exits the
                Anniversary locked-quantity mode and drops back to the plain
                single-share flow, where the buyer picks their own quantity and
                the BOGO bonus preview is shown. Display/selection only — the
                buyer bonus remains a server-side gift row (no double-mint). */}
            {singleShareSpecialActive && (
              <button
                key="SINGLE_SHARE_SPECIAL"
                type="button"
                onClick={() => clearBirthdayPackage()}
                style={{
                  flex: '1 1 160px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderRadius: '10px',
                  padding: '12px 14px',
                  background: '#2a2a2a',
                  color: '#ffffff',
                  border: '2px solid #4a4a4a',
                  fontWeight: 'bold',
                }}
              >
                <div style={{ fontSize: '14px', marginBottom: '2px', color: '#FFD700' }}>⭐ Single Share Special</div>
                <div style={{ fontSize: '12px', fontWeight: 'normal' }}>
                  Buy 1 → +{SINGLE_SHARE_SPECIAL_CONFIG.buyerBonusPerShare} FREE (you), +{SINGLE_SHARE_SPECIAL_CONFIG.uplineBonusPerShare} (sponsor), per share
                </div>
              </button>
            )}
            {(['BIRTHDAY_2', 'BIRTHDAY_25'] as BirthdayPackageId[]).map((pid) => {
              const active = birthdayPackage === pid;
              const p = BIRTHDAY_PROMO_CONFIG.packages[pid];
              return (
                <button
                  key={pid}
                  type="button"
                  onClick={() => setBirthdayPackage(pid)}
                  style={{
                    flex: '1 1 160px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    borderRadius: '10px',
                    padding: '12px 14px',
                    background: active ? '#d4af37' : '#2a2a2a',
                    color: active ? '#1a1a1a' : '#ffffff',
                    border: active ? '2px solid #d4af37' : '2px solid #4a4a4a',
                    fontWeight: 'bold',
                  }}
                >
                  <div style={{ fontSize: '14px', marginBottom: '2px' }}>{p.label}</div>
                  <div style={{ fontSize: '12px', fontWeight: 'normal' }}>
                    Buy {p.buyShares} → +{p.buyerBonus} bonus (you), +{p.uplineBonus} (sponsor)
                  </div>
                </button>
              );
            })}
          </div>
          <div style={{ color: '#ffffff', fontSize: '13px', marginBottom: '14px' }}>
            Quantity is locked to this package. Choose how many units to buy (max {BIRTHDAY_PROMO_CONFIG.maxQty}).
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
            <button
              type="button"
              onClick={() => setBirthdayQty(Math.max(BIRTHDAY_PROMO_CONFIG.minQty, birthdayQty - 1))}
              disabled={birthdayQty <= BIRTHDAY_PROMO_CONFIG.minQty}
              style={{ width: '40px', height: '40px', borderRadius: '8px', border: '1px solid #d4af37', background: birthdayQty <= BIRTHDAY_PROMO_CONFIG.minQty ? '#2a2a2a' : '#d4af37', color: birthdayQty <= BIRTHDAY_PROMO_CONFIG.minQty ? '#888' : '#1a1a1a', fontSize: '20px', fontWeight: 'bold', cursor: birthdayQty <= BIRTHDAY_PROMO_CONFIG.minQty ? 'not-allowed' : 'pointer' }}
            >−</button>
            <div style={{ minWidth: '56px', textAlign: 'center', color: '#ffffff', fontSize: '22px', fontWeight: 'bold' }}>×{birthdayQty}</div>
            <button
              type="button"
              onClick={() => setBirthdayQty(Math.min(BIRTHDAY_PROMO_CONFIG.maxQty, birthdayQty + 1))}
              disabled={birthdayQty >= BIRTHDAY_PROMO_CONFIG.maxQty}
              style={{ width: '40px', height: '40px', borderRadius: '8px', border: '1px solid #d4af37', background: birthdayQty >= BIRTHDAY_PROMO_CONFIG.maxQty ? '#2a2a2a' : '#d4af37', color: birthdayQty >= BIRTHDAY_PROMO_CONFIG.maxQty ? '#888' : '#1a1a1a', fontSize: '20px', fontWeight: 'bold', cursor: birthdayQty >= BIRTHDAY_PROMO_CONFIG.maxQty ? 'not-allowed' : 'pointer' }}
            >+</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div style={{ background: '#2a2a2a', borderRadius: '8px', padding: '10px' }}>
              <div style={{ color: '#d4af37', fontWeight: 'bold', fontSize: '18px' }}>{birthdayBreakdown.baseShares}</div>
              <div style={{ color: '#ffffff', fontSize: '12px' }}>Shares you buy</div>
            </div>
            <div style={{ background: '#2a2a2a', borderRadius: '8px', padding: '10px' }}>
              <div style={{ color: '#10b981', fontWeight: 'bold', fontSize: '18px' }}>+{birthdayBreakdown.buyerBonus}</div>
              <div style={{ color: '#ffffff', fontSize: '12px' }}>🎁 Your bonus shares</div>
            </div>
            <div style={{ background: '#2a2a2a', borderRadius: '8px', padding: '10px', gridColumn: '1 / span 2' }}>
              <div style={{ color: '#10b981', fontWeight: 'bold', fontSize: '18px' }}>+{birthdayBreakdown.uplineBonus}</div>
              <div style={{ color: '#ffffff', fontSize: '12px' }}>🎁 Gift to your direct sponsor (commission-free)</div>
            </div>
          </div>
        </div>
      )}

      {/* Single-Share Special promo notice (display-only; replaces the quick-select
          grid while the special is active). No pricing/minting change. */}
      {!birthdayMode && !pcapMode && singleShareSpecialActive && (
      <div style={{
        marginBottom: '24px',
        padding: '16px',
        backgroundColor: '#1a1508',
        border: '2px solid #D4AF37',
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        <div style={{ color: '#FFD700', fontSize: '16px', fontWeight: '700', marginBottom: '6px' }}>
          ⭐ Single Share Special
        </div>
        <div style={{ color: '#ffffff', fontSize: '14px', lineHeight: 1.5 }}>
          Buy 1 single share, get 1 <strong style={{ color: '#FFD700' }}>FREE</strong> — plus{' '}
          {SINGLE_SHARE_SPECIAL_CONFIG.uplineBonusPerShare} bonus share to your direct upline, per share bought.
        </div>
      </div>
      )}

      {/* NEW: Quick Share Selection Buttons (hidden in birthday locked-quantity
          mode, and while the Single-Share Special is active). */}
      {!birthdayMode && !pcapMode && !singleShareSpecialActive && (
      <div style={{ marginBottom: '24px' }}>
        <label style={{
          display: 'block',
          color: '#ffffff',
          marginBottom: '12px',
          fontSize: '16px',
          fontWeight: '600'
        }}>
          Quick Select Shares
        </label>
        <div className={styles.quickAmountGrid}>
          {quickShares.map(quickShareCount => {
            const shareAmount = currentPhase ? quickShareCount * currentPhase.price_per_share : 0;
            const displayAmount = selectedCurrency === 'ZAR' ?
              shareAmount * bankTransferConfig.exchangeRate * (1 + bankTransferConfig.transactionFee) :
              shareAmount;
            const currencySymbol = selectedCurrency === 'ZAR' ? 'R' : '$';

            return (
              <button
                key={quickShareCount}
                onClick={() => {
                  console.log('🔢 Quick shares clicked:', quickShareCount);
                  const currentShares = parseInt(shares) || 0;
                  const newShares = currentShares + quickShareCount;
                  setShares(newShares.toString());
                  // Auto-calculate amount
                  const calculatedAmount = calculateAmount(newShares.toString());
                  setAmount(calculatedAmount.toString());
                  console.log('✅ Shares set to:', newShares, 'Amount:', calculatedAmount);
                }}
                className={styles.quickAmountButton}
              >
                <div style={{ fontWeight: 'bold', fontSize: '16px' }}>+{quickShareCount} shares</div>
                <div style={{ fontSize: '14px', color: '#ffffff', marginTop: '2px' }}>
                  {currencySymbol}{displayAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      )}

      {/* Free-type share input only. Package modes (PCAP / retired Anniversary)
          must never render the old "locked to package" field — the chooser owns qty.
          Do not reintroduce that label or a disabled share box in pcapMode/birthdayMode. */}
      {!pcapMode && !birthdayMode && (
      <div style={{ marginBottom: '24px' }}>
        <label style={{
          display: 'block',
          color: '#ffffff',
          marginBottom: '8px',
          fontSize: '16px',
          fontWeight: '600'
        }}>
          Number of Shares to Purchase
          {selectedCurrency === 'ZAR' && (
            <span style={{ color: '#f59e0b', fontSize: '14px', marginLeft: '8px' }}>
              • Payment in ZAR
            </span>
          )}
        </label>
        <div style={{ position: 'relative' }}>
          <span style={{
            position: 'absolute',
            left: '16px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#ffffff',
            fontSize: '18px',
            fontWeight: 'bold'
          }}>📊</span>
          <input
            type="number"
            value={shares}
            onChange={(e) => {
              const newShares = e.target.value;
              setShares(newShares);
              const calculatedAmount = calculateAmount(newShares);
              setAmount(calculatedAmount.toString());
            }}
            placeholder="Enter number of shares (whole numbers only)"
            className={styles.customAmountInput}
            min="1"
            step="1"
          />
        </div>
        {shares && parseInt(shares) > 0 && currentPhase && (
          <div style={{
            marginTop: '8px',
            padding: '12px',
            backgroundColor: isSpecialPricingActive ? '#1e3a2e' : '#1f2937',
            border: isSpecialPricingActive ? '2px solid #f59e0b' : '1px solid #10b981',
            borderRadius: '6px',
            fontSize: '14px'
          }}>
            {/* Special Pricing Banner for User 106 */}
            {isSpecialPricingActive && (
              <div style={{
                backgroundColor: '#f59e0b',
                color: '#000',
                padding: '6px 12px',
                borderRadius: '4px',
                marginBottom: '8px',
                fontWeight: 'bold',
                fontSize: '13px',
                textAlign: 'center'
              }}>
                🧪 TEST MODE: Special $1 pricing for testing
              </div>
            )}
            {/* Show special pricing indicator for User 106 even when not active */}
            {isSpecialPricingUser && !isSpecialPricingActive && parseInt(shares) > 1 && (
              <div style={{
                backgroundColor: '#374151',
                color: '#ffffff',
                padding: '4px 8px',
                borderRadius: '4px',
                marginBottom: '8px',
                fontSize: '14px',
                textAlign: 'center'
              }}>
                💡 Tip: Select exactly 1 share to activate $1 test pricing
              </div>
            )}
            <div style={{ color: '#10b981', fontWeight: 'bold', marginBottom: '4px' }}>
              💰 Total Cost: ${calculateAmount(shares).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              {isSpecialPricingActive && (
                <span style={{ marginLeft: '8px', color: '#f59e0b', fontSize: '13px' }}>
                  (normally ${normalCost.toLocaleString(undefined, { maximumFractionDigits: 2 })})
                </span>
              )}
            </div>
            {selectedCurrency === 'ZAR' && (
              <div style={{ color: '#f59e0b', fontSize: '13px' }}>
                💱 ZAR Equivalent: R{(calculateAmount(shares) * bankTransferConfig.exchangeRate * (1 + bankTransferConfig.transactionFee)).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Bonus Tiers Display — folded in Production Capital + Anniversary package modes */}
      {!pcapMode && !birthdayMode && (
      <div style={{
        marginBottom: '24px',
        padding: '16px',
        backgroundColor: 'rgba(16, 185, 129, 0.05)',
        border: '1px solid rgba(16, 185, 129, 0.2)',
        borderRadius: '12px'
      }}>
        <h3 style={{
          color: '#10b981',
          fontWeight: 'bold',
          margin: '0 0 12px 0',
          fontSize: '16px',
          textAlign: 'center'
        }}>
          🎁 Bonus Share Tiers
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '8px'
        }}>
          {(() => {
            // Get the EXACT tier that matches the current share count
            const currentTier = getBonusTier(numShares);

            return getAllBonusTiers().reverse().map((tier, index) => {
              // Only highlight if this is the EXACT tier that matches
              const isActiveTier = currentTier && tier.minShares === currentTier.minShares;

              return (
                <div
                  key={index}
                  style={{
                    padding: '10px',
                    backgroundColor: isActiveTier ? 'rgba(16, 185, 129, 0.15)' : 'rgba(55, 65, 81, 0.3)',
                    border: isActiveTier ? '2px solid #10b981' : '1px solid #374151',
                    borderRadius: '8px',
                    textAlign: 'center',
                    transition: 'all 0.3s'
                  }}
                >
                  <div style={{
                    color: isActiveTier ? '#10b981' : '#9ca3af',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    {tier.minShares.toLocaleString()} shares
                  </div>
                  <div style={{
                    color: isActiveTier ? '#10b981' : '#6b7280',
                    fontSize: '13px',
                    marginTop: '4px'
                  }}>
                    +{tier.bonusShares.toLocaleString()} bonus
                  </div>
                  {isActiveTier && (
                    <div style={{ color: '#10b981', fontSize: '16px', marginTop: '4px' }}>✓</div>
                  )}
                </div>
              );
            });
          })()}
        </div>
        <div style={{
          marginTop: '12px',
          textAlign: 'center',
          color: '#ffffff',
          fontSize: '13px'
        }}>
          Purchase more shares to unlock higher bonus tiers!
        </div>
      </div>
      )}

      {/* Retired screenshot checkout chrome (commission apply + locked-share box).
          Do not reintroduce that amount-step UI. */}

      {/* Purchase Preview — free-type only. Package checkout uses the P1–P5 chooser. */}
      {!pcapMode && !birthdayMode && amount && parseFloat(amount) > 0 && (
        <div style={{
          backgroundColor: 'rgba(55, 65, 81, 0.5)',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <h3 style={{
            color: '#fbbf24',
            fontWeight: 'bold',
            margin: '0 0 16px 0',
            fontSize: '18px'
          }}>
            📋 Purchase Preview
          </h3>
          <div className={styles.purchasePreviewGrid}>
            {pcapMode && pcapBreakdown ? (
              <>
                <div className={styles.previewCard}>
                  <div style={{ color: '#3b82f6', fontSize: '24px', fontWeight: 'bold' }}>
                    {pcapBreakdown.paidShares.toLocaleString()}
                  </div>
                  <div style={{ color: '#ffffff', fontSize: '14px' }}>Base Shares</div>
                </div>
                <div className={styles.previewCard} style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                  <div style={{ color: '#10b981', fontSize: '24px', fontWeight: 'bold' }}>
                    +{pcapBreakdown.investorBonus.toLocaleString()}
                  </div>
                  <div style={{ color: '#10b981', fontSize: '14px' }}>Bonus Shares</div>
                </div>
                <div className={styles.previewCard} style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                  <div style={{ color: '#10b981', fontSize: '24px', fontWeight: 'bold' }}>
                    +{pcapBreakdown.sponsorBonus.toLocaleString()}
                  </div>
                  <div style={{ color: '#10b981', fontSize: '14px' }}>Upline Shares</div>
                </div>
                <div className={styles.previewCard} style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
                  <div style={{ color: '#f59e0b', fontSize: '24px', fontWeight: 'bold' }}>
                    {pcapBreakdown.investorTotal.toLocaleString()}
                  </div>
                  <div style={{ color: '#f59e0b', fontSize: '14px' }}>Total Shares</div>
                  <div style={{ color: '#9ca3af', fontSize: '11px', marginTop: '4px' }}>Base + bonus (you)</div>
                </div>
                <div className={styles.previewCard}>
                  <div style={{ color: '#10b981', fontSize: '24px', fontWeight: 'bold' }}>
                    {selectedCurrency === 'ZAR' ?
                      `R${(pcapBreakdown.priceUsd * bankTransferConfig.exchangeRate * (1 + bankTransferConfig.transactionFee)).toLocaleString(undefined, { maximumFractionDigits: 2 })}` :
                      `$${pcapBreakdown.priceUsd.toLocaleString()}`
                    }
                  </div>
                  <div style={{ color: '#ffffff', fontSize: '14px' }}>
                    Total Cost
                    {selectedCurrency === 'ZAR' && (
                      <div style={{ fontSize: '14px', color: '#f59e0b' }}>
                        (${pcapBreakdown.priceUsd.toLocaleString()} USD + fees)
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
            <div className={styles.previewCard}>
              <div style={{ color: '#3b82f6', fontSize: '24px', fontWeight: 'bold' }}>
                {numShares.toLocaleString()}
              </div>
              <div style={{ color: '#ffffff', fontSize: '14px' }}>Base Shares</div>
            </div>
            {displayBonusShares > 0 && (
              <div className={styles.previewCard} style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                <div style={{ color: '#10b981', fontSize: '24px', fontWeight: 'bold' }}>
                  +{displayBonusShares.toLocaleString()}
                </div>
                <div style={{ color: '#10b981', fontSize: '14px' }}>
                  🎁 {ssSpecialBuyerBonus > 0 ? 'Free Bonus Shares' : 'Bonus Shares'}
                </div>
              </div>
            )}
            <div className={styles.previewCard} style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
              <div style={{ color: '#f59e0b', fontSize: '24px', fontWeight: 'bold' }}>
                {displayTotalShares.toLocaleString()}
              </div>
              <div style={{ color: '#f59e0b', fontSize: '14px' }}>Total Shares</div>
            </div>
            <div className={styles.previewCard}>
              <div style={{ color: '#10b981', fontSize: '24px', fontWeight: 'bold' }}>
                {selectedCurrency === 'ZAR' ?
                  `R${totalZarAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}` :
                  `$${totalCost.toFixed(2)}`
                }
              </div>
              <div style={{ color: '#ffffff', fontSize: '14px' }}>
                Total Cost
                {selectedCurrency === 'ZAR' && (
                  <div style={{ fontSize: '14px', color: '#f59e0b' }}>
                    (${totalCost.toFixed(2)} USD + fees)
                  </div>
                )}
              </div>
            </div>
            <div className={styles.previewCard}>
              <div style={{ color: '#f59e0b', fontSize: '24px', fontWeight: 'bold' }}>
                {selectedCurrency === 'ZAR' ?
                  `R${(realTimePrice * bankTransferConfig.exchangeRate * (1 + bankTransferConfig.transactionFee)).toFixed(2)}` :
                  `$${realTimePrice.toFixed(2)}`
                }
              </div>
              <div style={{ color: '#ffffff', fontSize: '14px' }}>
                Price per Share
                {selectedCurrency === 'ZAR' && (
                  <div style={{ fontSize: '14px', color: '#f59e0b' }}>
                    (${realTimePrice.toFixed(2)} USD + fees)
                  </div>
                )}
              </div>
            </div>
              </>
            )}
          </div>

          {/* Bonus Share Information */}
          {pcapMode && pcapBreakdown ? (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ color: '#10b981', fontSize: '16px', fontWeight: 'bold' }}>
                Campaign package: you receive {pcapBreakdown.investorTotal.toLocaleString()} shares
                (paid {pcapBreakdown.paidShares.toLocaleString()} + bonus {pcapBreakdown.investorBonus.toLocaleString()}).
                Sponsor receives +{pcapBreakdown.sponsorBonus.toLocaleString()} (not counted in your total).
              </div>
            </div>
          ) : displayBonusShares > 0 && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ color: '#10b981', fontSize: '16px', fontWeight: 'bold' }}>
                {ssSpecialBuyerBonus > 0
                  ? `🎉 Single-Share Special: Buy ${numShares.toLocaleString()}, get ${ssSpecialBuyerBonus.toLocaleString()} FREE — ${displayTotalShares.toLocaleString()} shares total!`
                  : `🎉 Congratulations! You qualify for +${displayBonusShares.toLocaleString()} bonus shares!`}
              </div>
              <div style={{ color: '#ffffff', fontSize: '13px', marginTop: '4px' }}>
                Bonus shares are automatically added to your account after approval
              </div>
            </div>
          )}

          {/* Next Tier Incentive — folded for Production Capital, Anniversary, and SSS */}
          {!pcapMode && !birthdayMode && !singleShareSpecialActive && displayBonusShares === 0 && numShares > 0 && (() => {
            const nextTier = getNextBonusTier(numShares);
            if (nextTier) {
              const sharesNeeded = nextTier.minShares - numShares;
              return (
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: 'rgba(245, 158, 11, 0.1)',
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: '#f59e0b', fontSize: '14px', fontWeight: 'bold' }}>
                    💡 Purchase {sharesNeeded.toLocaleString()} more shares to unlock +{nextTier.bonusShares.toLocaleString()} bonus shares!
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}

      {/* One-Click Purchase Options */}
      {savedPaymentMethods.length > 0 && amount && parseFloat(amount) > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{
            color: '#ffffff',
            fontSize: '16px',
            fontWeight: '600',
            marginBottom: '12px'
          }}>
            ⚡ One-Click Purchase
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {savedPaymentMethods.slice(0, 2).map(method => (
              <button
                key={method.id}
                onClick={() => onOneClickPurchase(method)}
                className={styles.oneClickButton}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '20px' }}>
                    {method.type === 'crypto' ? '💳' : '🏦'}
                  </span>
                  <div style={{ textAlign: 'left' }}>
                    <div>{method.name}</div>
                    <div style={{ fontSize: '13px', color: '#ffffff' }}>
                      {method.type === 'crypto' ? method.network?.name : 'Bank Transfer'}
                    </div>
                  </div>
                </div>
                <span style={{ fontSize: '16px' }}>⚡</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Continue Button */}
      <button
        onClick={onSubmit}
        disabled={!amount || parseFloat(amount) <= 0}
        className={styles.continueButton}
      >
        Continue to Payment Method →
      </button>
    </div>
  );
};

// DEPRECATED (v4.3.61): Yoco Payment Step removed — Yoco integration cancelled
// Historical component code removed. See version history for reference.

export default SharePurchaseFlow;

// ===== DEPRECATED YOCO COMPONENT REMOVED (v4.3.62) =====
// The YocoPaymentStep component was here but was removed because
// block comments containing JSX {/* */} syntax cause esbuild to
// prematurely close the outer comment block, breaking the build.
// Historical Yoco data is preserved in yoco_payment_transactions table.
//
// Dead Yoco code deleted — see git history for v4.3.60 if needed


